import type { LocationPageConfig } from './locationPageConfig.types.js';
import { KOCHI_LOCATIONS } from '../../constants/locations.js';
import { mergeHyperLocalFaqs } from '../../utils/locationHyperLocalFaqs.js';
import {
  NEIGHBOURHOOD_HOME_CLEANING_ALIASES,
  NEIGHBOURHOOD_LOCATION_CONFIGS,
} from './neighbourhoodLocationConfigs.js';

interface SeoContentArgs {
  locationName: string;
  introParagraphs: string[];
  services: {
    lead: string;
    bullets: string[];
  };
  lifestyle: string[];
  coverage: string[];
  booking: string[];
  sameDay?: string[];
  conclusion: string;
}

interface FaqArgs {
  locationName: string;
  responseTime: string;
  areasMention: string[];
  pricingDetail: string;
  bookingDetail: string;
  extraQuestion?: {
    question: string;
    answer: string;
  };
  extraQuestions?: Array<{
    question: string;
    answer: string;
  }>;
  preparationDetail?: string;
}

const createSeoContent = ({
  locationName,
  introParagraphs,
  services,
  lifestyle,
  coverage,
  booking,
  sameDay,
  conclusion,
}: SeoContentArgs): LocationPageConfig['seo'] => {
  const sections = [
    {
      heading: `Cleaning Services Available in ${locationName}`,
      paragraphs: [services.lead],
      bulletList: services.bullets,
    },
    {
      heading: `Why ${locationName} Residents Choose Professional Cleaners`,
      paragraphs: lifestyle,
    },
    {
      heading: `Neighborhood Coverage in ${locationName}`,
      paragraphs: coverage,
    },
    {
      heading: `Fast Booking & Same-Day Slots`,
      paragraphs: sameDay ? [...sameDay, ...booking] : booking,
    },
  ];

  return {
    introHeading: `Professional Cleaning Service in ${locationName}: 2025 Guide`,
    introParagraphs,
    sections,
    conclusion,
  };
};

const createFaq = ({
  locationName,
  responseTime,
  areasMention,
  pricingDetail,
  bookingDetail,
  extraQuestion,
  extraQuestions,
  preparationDetail,
}: FaqArgs) => {
  const areaSentence = areasMention.length
    ? areasMention.slice(0, 2).join(' and ')
    : `${locationName} and nearby areas`;

  const faq = [
    {
      question: `How fast can I schedule a cleaning in ${locationName}?`,
      answer: responseTime,
    },
    {
      question: `Do you cover locations like ${areaSentence}?`,
      answer: `Yes, our ${locationName} crew serves ${areasMention.join(', ')} and neighboring communities. Call us if you are slightly outside the list and we will confirm availability immediately.`,
    },
    {
      question: `How do you price deep cleaning in ${locationName}?`,
      answer: pricingDetail,
    },
    {
      question: `What is the easiest way to book a cleaning slot?`,
      answer: bookingDetail,
    },
  ];

  if (preparationDetail) {
    faq.push({
      question: 'Do I need to prepare my space before the crew arrives?',
      answer: preparationDetail,
    });
  }

  if (extraQuestion) {
    faq.push(extraQuestion);
  }
  if (extraQuestions?.length) {
    faq.push(...extraQuestions);
  }

  return mergeHyperLocalFaqs(faq, { locationName, areasMention });
};

export const locationConfigs: Record<string, LocationPageConfig> = {
  edappally: {
    slug: 'cleaning-service-edappally',
    locationName: 'Edappally',
    heroImage: '/images/hero-edappally.png',
    hero: {
      eyebrow: 'Serving Edappally Metro, Oberon Mall & NH Bypass',
      title: 'Cleaning Service in Edappally',
      description:
        'Fast home and office cleaning near Edappally Metro, Oberon Mall, and Changampuzha Park. Same-day slots with transparent online pricing.',
      highlights: [
        { icon: 'Train', text: 'Steps from Edappally Metro' },
        { icon: 'Clock', text: 'Same-day availability' },
        { icon: 'Star', text: '4.9★ rated local crew' },
      ],
      bookNowLocation: 'edappally',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Train',
        title: 'Perfect for Metro Commuters',
        description:
          'Sync your cleaning with metro timings. Book while you commute and return to a spotless apartment near Edappally Junction.',
      },
      {
        icon: 'Shield',
        title: 'Trusted Around Oberon Mall',
        description:
          'We maintain recurring schedules for families in Ponekkara, Vennala, and Mamangalam. Background-verified staff with supervisor check-ins.',
      },
      {
        icon: 'Calendar',
        title: 'Flexible Weekday & Weekend Slots',
        description:
          'Early mornings before office or late evenings after traffic – choose any slot that fits your Edappally lifestyle.',
      },
    ],
    serviceAreas: [
      'Edappally Metro Station',
      'Oberon Mall Road',
      'Changampuzha Park',
      'Rajagiri Hospital Zone',
      'Ponekkara',
      'Edappally Junction',
      'Vennala',
      'NH Bypass Apartments',
      'Mamangalam',
      'Palarivattom',
      'JLN Stadium Corridor',
      'Kaloor',
    ],
    serviceAreaCallout: {
      emoji: '🚇',
      title: 'Living within 5 km of Edappally Metro?',
      description:
        'We cover every apartment society, villa community, and commercial space across the metro corridor and NH Bypass sector.',
    },
    reviews: [
      {
        name: 'Anjali Thomas',
        location: 'Edappally Metro Residency',
        rating: 5,
        text: 'Booked while I was at work and the team finished before I reached home. Absolute lifesaver for busy commuters!',
        date: 'October 2025',
      },
      {
        name: 'Vivek Krishnan',
        location: 'Oberon Mall Vicinity',
        rating: 5,
        text: 'Precise, punctual, and polite. They deep cleaned our 3BHK near Oberon Mall better than any other service in Kochi.',
        date: 'September 2025',
      },
      {
        name: 'Lakshmi Nair',
        location: 'Changampuzha Park Road',
        rating: 5,
        text: 'Loved the transparent pricing and WhatsApp updates. House looked brand new after the kitchen deep cleaning.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: "Book Edappally's Most Trusted Cleaning Crew",
      description: 'Join 10,000+ Kochi residents who rely on Tools & Bubbles for spotless homes and offices.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'Edappally',
      introParagraphs: [
        'Searching for a professional cleaning service near Edappally Metro? Our dedicated crew manages apartments and villas across Oberon Mall, Changampuzha Park, and NH Bypass high-rises every single day.',
        'We understand the metro rush, busy families, and gated communities that define Edappally, so our schedules align with office timings, school runs, and resident association guidelines.',
      ],
      services: {
        lead: 'Choose a ready-made deep cleaning package or ask for a custom checklist for flats along Mamangalam, Ponekkara, and Vennala.',
        bullets: [
          'Deep house cleaning for apartments near Oberon Mall, Lulu Mall, and NH Bypass',
          'Kitchen and chimney degreasing for high-rise homes along Ponekkara and Vennala',
          'Bathroom descaling and sanitisation for residences near Changampuzha Park',
          'Sofa and upholstery shampooing for premium homes in Mamangalam and Elamakkara',
          'Move-in and post-renovation clean-ups for new towers on Edappally Junction',
          'Office and clinic cleaning for businesses around the metro corridor',
        ],
      },
      lifestyle: [
        "Edappally is one of Kochi's busiest residential zones with constant metro connectivity and mall footfall. Professional cleaners ensure dust, pollution, and weekend crowds do not creep into your home.",
        'Most clients prefer weekday slots while they are at work, letting our supervisors lock up after service and send photo proof on WhatsApp.',
      ],
      coverage: [
        'Our Edappally team serves every neighbourhood from Rajagiri Hospital to Kaloor. If your society needs gate pass coordination, we handle that before arriving on-site.',
      ],
      sameDay: [
        'Need cleaning today? With teams stationed near the metro station and NH Bypass, we can usually dispatch a crew within 3–4 hours for urgent requests.',
      ],
      booking: [
        'Book online in under a minute: pick your service, choose a slot, pay securely, and receive instant confirmation plus reminder SMS.',
      ],
      conclusion:
        "Ready to experience Edappally's most reliable cleaning service? Book online now or call +91 9995216660 for a quick quote.",
    }),
    faq: createFaq({
      locationName: 'Edappally',
      responseTime:
        'Same-day slots are often available, especially for bookings made before 1 PM. Weekend slots fill faster, so we recommend booking 24 hours ahead.',
      areasMention: ['Oberon Mall', 'Changampuzha Park', 'Rajagiri Hospital', 'NH Bypass Apartments'],
      pricingDetail:
        'Pricing depends on property size and service depth. View live pricing on our booking form—no hidden charges, and seasonal discounts appear automatically.',
      bookingDetail:
        'Click the Book Now button, choose a service, and secure your slot. You will receive SMS, email, and WhatsApp updates from start to finish.',
      preparationDetail:
        'Please secure valuables and provide water/electricity access. If your society needs passes, share details during booking and we will arrange it.',
      extraQuestion: {
        question: 'Can I book while I am at the office?',
        answer:
          'Absolutely. Many Edappally commuters book while travelling. We coordinate via phone or WhatsApp and lock up safely on completion.',
      },
      extraQuestions: [
        {
          question: 'Do you offer same-day house cleaning near Edappally Metro?',
          answer:
            'Yes. Book before 1 PM and we usually dispatch a crew the same day to apartments around Edappally Metro, Oberon Mall, and the NH Bypass. Instant confirmation is on the website — no phone tag.',
        },
      ],
    }),
    localLandmarks: [
      'Edappally Metro Station',
      'Oberon Mall',
      'Changampuzha Park',
      'Rajagiri College & Hospital',
      'Lulu Mall (NH Bypass)',
      'Edappally Junction',
      'JLN Stadium',
    ],
    pricingTable: [
      {
        service: 'Deep cleaning (apartment 2BHK)',
        fromAmount: 2999,
        notes: 'Includes kitchen, bathrooms, rooms',
      },
      { service: 'Deep cleaning (apartment 3BHK)', fromAmount: 3999, notes: 'Full home; add-ons extra' },
      {
        service: 'Sofa cleaning',
        fromAmount: 999,
        notes: 'Per seat; 3-seater {fromPrice1499}',
        notesFromAmount: 1499,
      },
      { service: 'Kitchen deep cleaning', fromAmount: 1499, notes: 'Chimney & cabinets included' },
      { service: 'Bathroom deep cleaning', fromAmount: 999, notes: 'Anti-fungal treatment' },
      { service: 'Office cleaning', fromAmount: 4500, notes: 'Per visit; size-dependent' },
    ],
  },

  kakkanad: {
    slug: 'cleaning-service-kakkanad',
    locationName: 'Kakkanad',
    heroImage: '/images/hero-kakkanad.png',
    hero: {
      eyebrow: 'Serving InfoPark, SmartCity & Seaport-Airport Road',
      title: 'Cleaning Service in Kakkanad',
      description:
        'Professional deep cleaning, sofa shampooing, and office housekeeping for InfoPark apartments and villas across Kakkanad.',
      highlights: [
        { icon: 'Clock', text: 'Same-day response' },
        { icon: 'Briefcase', text: 'Corporate-ready cleaning' },
        { icon: 'Shield', text: 'Background-verified staff' },
      ],
      bookNowLocation: 'kakkanad',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Calendar',
        title: 'Book in 60 Seconds',
        description:
          'No calls, no WhatsApp follow ups. InfoPark professionals love our instant web booking with upfront pricing.',
      },
      {
        icon: 'Building',
        title: 'Ideal for High-Rise Apartments',
        description:
          'We routinely serve Skyline, Confident, and Prestige towers with equipment sized for elevators and service lifts.',
      },
      {
        icon: 'Sparkles',
        title: 'IT-Friendly Scheduling',
        description:
          'Night shifts and weekend sprints? Our Kakkanad crew handles odd-hour cleanups for tech teams and co-working spaces.',
      },
    ],
    serviceAreas: [
      'InfoPark Phase 1 & 2',
      'SmartCity Kochi',
      'Seaport-Airport Road',
      'Thrikkakara',
      'Kakkanad Civil Station',
      'Chittethukara',
      'Vazhakkala',
      'Kakkanad HMT Colony',
      'Padamugal',
      'Brahmapuram',
      'NGO Quarters',
      'Kakkanad Junction',
    ],
    serviceAreaCallout: {
      emoji: '💻',
      title: 'Working late at InfoPark?',
      description:
        'Book while you are at your desk. We coordinate access with security and finish before you reach home.',
    },
    reviews: [
      {
        name: 'Rahul Kumar',
        location: 'InfoPark Phase 2',
        rating: 5,
        text: 'Super fast booking and spotless results. Perfect for busy software engineers who cannot wait around.',
        date: 'October 2025',
      },
      {
        name: 'Priya Menon',
        location: 'Seaport-Airport Road',
        rating: 5,
        text: 'Our villa looks brand new after their deep cleaning session. Loved the eco-friendly products they used.',
        date: 'September 2025',
      },
      {
        name: 'Suresh Nair',
        location: 'HMT Colony Kakkanad',
        rating: 5,
        text: 'Professional team with supervisor oversight. They handled a complex post-renovation cleanup with ease.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Ready for a Hotel-Grade Finish in Kakkanad?',
      description:
        'Book today and let our InfoPark-specialised crew handle your home or office without disrupting your workday.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'Kakkanad',
      introParagraphs: [
        "Kakkanad is the heart of Kochi's IT corridor, and the demand for reliable cleaning services grows with every new apartment tower. Tools & Bubbles has dedicated teams stationed near InfoPark and SmartCity for rapid deployment.",
        'Whether you manage a 3BHK on Seaport-Airport Road or a co-working floor inside InfoPark, our technicians bring industrial-grade equipment and trained staff to every booking.',
      ],
      services: {
        lead: 'Select from residential deep cleaning packages, corporate housekeeping shifts, or add-on sofa, carpet, and water tank services.',
        bullets: [
          'Full home deep cleaning for apartments in InfoPark and SmartCity campuses',
          'Post-move cleaning for villas in Thrikkakara, Vazhakkala, and Civil Station',
          'Office pantry, workstation, and restroom cleaning for tech companies',
          'Sofa, recliner, and chair sanitisation for work-from-home setups',
          'Kitchen degreasing for high usage homes near Seaport-Airport Road',
          'Water tank and sump cleaning for gated communities in Kakkanad',
        ],
      },
      lifestyle: [
        'Work-from-home and hybrid teams in Kakkanad often lack time to manage detailed cleaning. Outsourcing to a professional crew keeps your space healthy without sacrificing rest days.',
        'Our supervisors maintain SOPs tailored for IT apartments, ensuring network cables, monitors, and smart devices remain untouched.',
      ],
      coverage: [
        "From Brahmapuram to Chittethukara, Kakkanad's residential pockets fall within a 20-minute drive for our crew. We also support campus hostels and guest houses on request.",
      ],
      sameDay: [
        'Need an urgent cleaning before guests arrive? Same-day or next-morning slots are usually available for bookings confirmed before 11 AM.',
      ],
      booking: [
        'Use our online scheduler to lock a slot that fits your shift timings. Instant confirmation is followed by supervisor coordination via WhatsApp.',
      ],
      conclusion:
        'Your Kakkanad home or office deserves a spotless finish. Reserve your preferred slot online or speak to us at +91 9995216660.',
    }),
    faq: createFaq({
      locationName: 'Kakkanad',
      responseTime:
        'We maintain crews inside Kakkanad, so same-day or early next-day visits are usually guaranteed.',
      areasMention: ['InfoPark', 'SmartCity', 'Seaport-Airport Road', 'Thrikkakara'],
      pricingDetail:
        'Apartment size, service depth, and add-ons define pricing. View transparent rates online; corporate contracts receive GST invoices.',
      bookingDetail:
        'Tap the Book Now button, choose a service, and pay online. Corporate clients can request monthly billing through our support desk.',
      preparationDetail:
        'Please arrange building access cards or visitor passes. For office clean-ups, share workstation guidelines beforehand.',
      extraQuestion: {
        question: 'Do you work on late night schedules?',
        answer:
          'Yes, we support late evening and night slots for IT offices with prior confirmation and supervisor oversight.',
      },
      extraQuestions: [
        {
          question:
            "How long does deep cleaning take for an IT professional's apartment in Kakkanad Infopark?",
          answer:
            'A typical 2BHK near Infopark Phase 1 or 2 takes 4–6 hours. Book a weekday afternoon or evening slot online so you never spend Sunday on grout. Larger 3BHKs and villas on Seaport-Airport Road usually need 6–8 hours with the same insured, in-house crew.',
        },
        {
          question: 'Can you clean after office hours for Infopark residents?',
          answer:
            'Yes. Many software engineers book 6–9 PM or weekend slots. We coordinate society visitor passes and send WhatsApp photo proof when we lock up.',
        },
      ],
    }),
    localLandmarks: [
      'InfoPark Phase 1 & 2',
      'SmartCity Kochi',
      'Seaport-Airport Road',
      'Kakkanad Civil Station',
      'Lulu Mall (Kakkanad)',
      'Cochin Special Economic Zone',
      'Brahmapuram',
    ],
    pricingTable: [
      { service: 'Deep cleaning (2BHK apartment)', fromAmount: 2999, notes: 'InfoPark & SmartCity areas' },
      { service: 'Deep cleaning (3BHK / villa)', fromAmount: 3999, notes: 'Full home; add-ons extra' },
      { service: 'Sofa cleaning', fromAmount: 999, notes: 'Per seat' },
      {
        service: 'Office / workspace cleaning',
        fromAmount: 4500,
        notes: 'Per visit; corporate billing available',
      },
      { service: 'Kitchen deep cleaning', fromAmount: 1499, notes: 'Chimney degreasing included' },
      { service: 'Water tank cleaning', fromAmount: 1500, notes: 'Sludge removal & disinfection' },
    ],
  },

  kaloor: {
    slug: 'cleaning-service-kaloor',
    locationName: 'Kaloor',
    heroImage: '/images/hero-home.png',
    hero: {
      eyebrow: 'Serving Kaloor Junction, JLN Stadium & Kaloor-Kadavanthra Road',
      title: 'Cleaning Service in Kaloor',
      description:
        'Top-rated cleaning professionals for apartments and businesses around Kaloor Junction, JLN Stadium, and Deshabhimani Road.',
      highlights: [
        { icon: 'Bus', text: 'Near JLN Stadium Metro' },
        { icon: 'Clock', text: '24-hour response window' },
        { icon: 'Shield', text: 'Police-verified cleaners' },
      ],
      bookNowLocation: 'kaloor',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Users',
        title: 'Tailored for Stadium Traffic',
        description:
          'Match cleaning slots with match days and events at JLN Stadium. We work around traffic diversions and parking restrictions.',
      },
      {
        icon: 'HeartHandshake',
        title: 'Trusted by Long-Term Residents',
        description:
          'Families on Kaloor-Kadavanthra Road rely on our recurring maintenance plans to keep homes fresh year-round.',
      },
      {
        icon: 'Clock',
        title: 'Early Morning Availability',
        description:
          'Beat Kaloor traffic by booking 6 AM or 7 AM slots. Ideal for professionals heading to MG Road or InfoPark.',
      },
    ],
    serviceAreas: [
      'Kaloor Junction',
      'JLN Stadium Road',
      'Deshabhimani Junction',
      'Kaloor-Kadavanthra Road',
      'SRM Road',
      'Kacheripady',
      'Pullepady',
      'North Railway Station',
      'Kathrikadavu',
      'Elamkulam Extension',
      'Pottammal Road',
      'Kaloor Market Area',
    ],
    serviceAreaCallout: {
      emoji: '🚦',
      title: 'Live near Kaloor Junction signals?',
      description: 'We schedule crews to arrive before rush hour and secure parking permissions in advance.',
    },
    reviews: [
      {
        name: 'George Mathew',
        location: 'Kaloor-Kadavanthra Road',
        rating: 5,
        text: 'Excellent deep cleaning before Onam guests arrived. Team worked around our busy road without delays.',
        date: 'September 2025',
      },
      {
        name: 'Neha Pillai',
        location: 'JLN Stadium View Apartments',
        rating: 5,
        text: 'Booked after a late-night football match and they arrived at 6 AM sharp. Bathrooms and kitchen look new.',
        date: 'August 2025',
      },
      {
        name: 'Mohammed Rafi',
        location: 'SRM Road',
        rating: 5,
        text: 'Great value for money. They even helped coordinate building permission with our association.',
        date: 'July 2025',
      },
    ],
    cta: {
      title: "Book Kaloor's Go-To Cleaning Team Today",
      description:
        'From stadium-facing apartments to bustling commercial streets, we keep Kaloor spotless on your schedule.',
      phoneText: 'Speak to a Specialist',
    },
    seo: createSeoContent({
      locationName: 'Kaloor',
      introParagraphs: [
        "Kaloor is one of Kochi's liveliest intersections, home to JLN Stadium, Kaloor Market, and daily metro commuters. A professional cleaning service keeps dust and traffic pollution out of your living space.",
        'Tools & Bubbles assigns a dedicated Kaloor crew that understands building access, parking restrictions, and association guidelines along Kaloor-Kadavanthra Road.',
      ],
      services: {
        lead: "Choose from residential, commercial, and event-focused cleaning packages customised for Kaloor's mixed-use neighbourhoods.",
        bullets: [
          'Deep cleaning for apartments along Kaloor-Kadavanthra Road and SRM Road',
          'Quick turnaround cleaning for rental flats near JLN Stadium',
          'Sofa and mattress sanitising for homes near Deshabhimani Junction',
          'Commercial cleaning for clinics, coaching centres, and showrooms',
          'Bathroom descaling for older buildings around Kacheripady',
          'Move-in cleaning for new high-rises near North Railway Station',
        ],
      },
      lifestyle: [
        'Event days at JLN Stadium bring extra dust and footfall. Our teams schedule pre-event and post-event cleaning to keep your apartment guest-ready.',
        'Many Kaloor residents prefer early morning or late evening slots to avoid traffic congestion. We have crews assigned exclusively for those timings.',
      ],
      coverage: [
        'We serve every lane between Kathrikadavu and Pullepady, including older independent houses that need delicate handling.',
      ],
      sameDay: [
        'Because crews are already stationed nearby, same-day assistance is usually available when you contact us before afternoon peak hours.',
      ],
      booking: [
        'Lock your slot online or via WhatsApp. Our system sends reminders, crew details, and completion photos for complete transparency.',
      ],
      conclusion:
        'Keep your Kaloor home or office guest-ready year-round. Book now or call +91 9995216660 for a tailored plan.',
    }),
    faq: createFaq({
      locationName: 'Kaloor',
      responseTime: 'We can reach most Kaloor addresses within a few hours, especially for morning slots.',
      areasMention: ['JLN Stadium', 'Deshabhimani', 'Kathrikadavu', 'Kaloor Market'],
      pricingDetail:
        'Apartments and standalone houses are priced by square footage and complexity. Event-related clean-ups may carry a small rush fee for night work.',
      bookingDetail:
        'Use the online booking form or call us directly. We will confirm access instructions and share crew details instantly.',
      preparationDetail:
        'Keep parking space ready or share association guidelines so we can arrange alternate parking before arrival.',
      extraQuestion: {
        question: 'Can you clean right after a stadium event?',
        answer:
          'Yes, book a slot in advance and we will deploy a night or early morning crew to handle the post-event mess.',
      },
      extraQuestions: [
        {
          question: 'Do you clean villas and apartments near JLN Stadium in Kaloor?',
          answer:
            'Yes. Our Kaloor crew covers JLN Stadium apartments, Deshabhimani Road, Kathrikadavu, and Kaloor Market lanes. Same-day slots are common for morning bookings.',
        },
      ],
    }),
    localLandmarks: [
      'JLN Stadium (Jawaharlal Nehru Stadium)',
      'Kaloor Metro Station',
      'Kaloor Market',
      'Kaloor-Kadavanthra Road',
      'Deshabhimani Junction',
      'North Railway Station',
      'SRM Road',
    ],
    pricingTable: [
      { service: 'Deep cleaning (2BHK)', fromAmount: 2999, notes: 'Apartments near JLN Stadium' },
      { service: 'Deep cleaning (3BHK)', fromAmount: 3999, notes: 'Full home' },
      { service: 'Sofa cleaning', fromAmount: 999, notes: 'Per seat' },
      { service: 'Bathroom deep cleaning', fromAmount: 999, notes: 'Anti-fungal' },
      { service: 'Commercial / clinic cleaning', fromAmount: 4500, notes: 'Per visit' },
    ],
  },

  kalamassery: {
    slug: 'cleaning-service-kalamassery',
    locationName: 'Kalamassery',
    heroImage: '/images/hero-apartment.png',
    hero: {
      eyebrow: 'Serving CUSAT, HMT Colony & Pathadipalam Metro',
      title: 'Cleaning Service in Kalamassery',
      description:
        'Industrial-strength cleaning for homes, hostels, and offices across Kalamassery, including CUSAT campus and Kalamassery Municipality.',
      highlights: [
        { icon: 'Train', text: 'Pathadipalam & Kalamassery Metro' },
        { icon: 'Anchor', text: 'Industrial-grade equipment' },
        { icon: 'Clock', text: 'Rapid scheduling' },
      ],
      bookNowLocation: 'kalamassery',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Building',
        title: 'Fits Residential & Industrial Needs',
        description:
          'Whether you manage a CUSAT hostel room or an HMT colony villa, we carry precise tools and eco-friendly chemicals for each surface.',
      },
      {
        icon: 'Sparkles',
        title: 'Post-Industrial Dust Handling',
        description:
          "Kalamassery's factories can leave fine dust behind. Our HEPA filtration machines remove it safely from homes and offices.",
      },
      {
        icon: 'Calendar',
        title: 'Weekend & Hostel-Friendly Slots',
        description:
          'We coordinate with hostel wardens, industrial security, and association offices for seamless service.',
      },
    ],
    serviceAreas: [
      'CUSAT Campus',
      'Kalamassery Municipality',
      'HMT Colony',
      'Pathadipalam',
      'Premier Junction',
      'Medical College Road',
      'Kalamassery Town',
      'Muttom',
      'Companypady',
      'Cheranalloor',
      'Thrikkakara Extension',
      'Kalamassery Industrial Estate',
    ],
    serviceAreaCallout: {
      emoji: '🏭',
      title: 'Running an industrial unit or student hostel?',
      description:
        'Ask for recurring housekeeping solutions with monthly supervisor audits and consumables management.',
    },
    reviews: [
      {
        name: 'Dr. Simi Joseph',
        location: 'CUSAT Staff Quarters',
        rating: 5,
        text: 'They handled a full-villa cleanup before we moved in. Polite staff and perfect attention to detail.',
        date: 'September 2025',
      },
      {
        name: 'Arun Prakash',
        location: 'Kalamassery Industrial Estate',
        rating: 5,
        text: 'Our office needed monthly deep cleaning. The team followed safety compliance and delivered spotless results.',
        date: 'August 2025',
      },
      {
        name: 'Meera Nair',
        location: 'HMT Colony',
        rating: 5,
        text: 'Affordable, reliable, and fast. Their sofa cleaning removed stubborn stains without damaging fabric.',
        date: 'July 2025',
      },
    ],
    cta: {
      title: 'Keep Kalamassery Homes & Workspaces Spotless',
      description:
        'Book a one-time deep clean or set up a recurring maintenance plan tailored for your property.',
      phoneText: 'Chat with Our Team',
    },
    seo: createSeoContent({
      locationName: 'Kalamassery',
      introParagraphs: [
        'Kalamassery blends student hostels, industrial estates, and growing residential pockets. A standard housekeeping routine rarely keeps up with dust and footfall from nearby factories.',
        'Tools & Bubbles uses commercial-grade equipment and experienced staff to manage everything from hostel corridors to duplex villas near CUSAT.',
      ],
      services: {
        lead: 'Mix and match apartment deep cleaning, hostel sanitisation, and industrial office maintenance under one service partner.',
        bullets: [
          'Deep cleaning for apartments and villas near CUSAT and Kalamassery Town',
          'Hostel room sanitisation with mattress, curtain, and bathroom focus',
          'Commercial floor scrubbing for Kalamassery Industrial Estate offices',
          'Kitchen degreasing for eateries and canteens on Medical College Road',
          'Sofa and recliner shampooing for homes around HMT Colony',
          'Water tank and sump cleaning for gated communities near Pathadipalam',
        ],
      },
      lifestyle: [
        'With constant industrial traffic, dust and oil residues can build up quickly. Regular professional cleaning keeps indoor air quality healthy.',
        'Students and working professionals appreciate our flexible scheduling that works around class timings and shift patterns.',
      ],
      coverage: [
        'We cover Companypady to Cheranalloor and coordinate with security desks for access permissions wherever required.',
      ],
      sameDay: [
        'Emergency clean-up before inspections? Same-day slots are often available with 4 hours notice.',
      ],
      booking: [
        'Book online or request a site visit for large industrial facilities. Detailed checklists and safety briefings are provided before work begins.',
      ],
      conclusion:
        "Partner with a cleaning team that understands Kalamassery's unique mix of residential and industrial spaces. Book now or call +91 9995216660.",
    }),
    localLandmarks: [
      'CUSAT (Cochin University)',
      'Pathadipalam Metro Station',
      'Kalamassery Industrial Estate',
      'HMT Colony',
      'Medical College (Kochi)',
      'Premier Junction',
      'Kalamassery Municipality',
    ],
    pricingTable: [
      { service: 'Deep cleaning (apartment)', fromAmount: 2999, notes: '2BHK; CUSAT & HMT areas' },
      { service: 'Deep cleaning (villa / hostel block)', fromAmount: 4499, notes: 'Larger spaces' },
      { service: 'Sofa cleaning', fromAmount: 999, notes: 'Per seat' },
      { service: 'Office / industrial unit cleaning', fromAmount: 5000, notes: 'Recurring plans available' },
      { service: 'Kitchen deep cleaning', fromAmount: 1499, notes: 'Canteens & homes' },
    ],
    faq: createFaq({
      locationName: 'Kalamassery',
      responseTime:
        'We can usually reach Kalamassery customers the same day, with faster turnaround near Pathadipalam and CUSAT.',
      areasMention: ['CUSAT', 'HMT Colony', 'Pathadipalam', 'Industrial Estate'],
      pricingDetail:
        'Hostel rooms, apartments, and industrial offices have different checklists. Pricing is transparent and shared before confirmation.',
      bookingDetail:
        'Use our online booking system or call for a bulk contract. We can share compliance certificates for industrial plants.',
      preparationDetail:
        'Ensure machinery areas are powered down if needed and inform hostel wardens/security about our arrival.',
      extraQuestion: {
        question: 'Do you provide consumables for recurring contracts?',
        answer:
          'Yes, monthly contracts include replenishment of cleaning chemicals and tools as per agreement.',
      },
    }),
  },

  'mg-road': {
    slug: 'cleaning-service-mg-road',
    locationName: 'M.G. Road, Kochi',
    heroImage: '/images/hero-mg-road.png',
    hero: {
      eyebrow: 'Serving M.G. Road, Menaka & Marine Drive Connector',
      title: 'Cleaning Service on M.G. Road Kochi',
      description:
        'Premium cleaning for high-street apartments, showrooms, and offices along M.G. Road and Menaka Junction.',
      highlights: [
        { icon: 'Briefcase', text: 'Retail & office specialists' },
        { icon: 'Clock', text: 'Before/after business hours' },
        { icon: 'Sparkles', text: 'Spotless display-ready finish' },
      ],
      bookNowLocation: 'mg-road',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Building',
        title: 'Showroom Grade Cleaning',
        description:
          'Glass fronts, mannequins, and product displays cleaned with microfiber-safe techniques so you open doors spotless every morning.',
      },
      {
        icon: 'Clock',
        title: 'Business Hour Flexibility',
        description:
          'We schedule 7 AM or 9 PM slots to avoid customer traffic and metro rush around M.G. Road.',
      },
      {
        icon: 'Shield',
        title: 'Confidential & Secure',
        description:
          'Bank branches, law offices, and jewelers trust us with discreet, NDA-covered service teams.',
      },
    ],
    serviceAreas: [
      'Menaka Junction',
      'Jos Junction',
      'Pallimukku',
      'Shenoys',
      'Padma Theatre Zone',
      'Marine Drive Connector',
      'Banerji Road Extension',
      'Woodlands Junction',
      'D.H. Road',
      'Ravipuram',
      'TDM Hall',
      'Sahodaran Ayyappan Road (MG Road stretch)',
    ],
    serviceAreaCallout: {
      emoji: '🛍️',
      title: 'Running a store on M.G. Road?',
      description:
        'Request daily, weekly, or monthly cleaning sessions that keep your display area pristine before doors open.',
    },
    reviews: [
      {
        name: 'Harish Menon',
        location: 'Menaka Showroom',
        rating: 5,
        text: 'We schedule cleaning at 7 AM daily. Floors, glass, and fitting rooms stay immaculate without interrupting business.',
        date: 'October 2025',
      },
      {
        name: 'Anita Varma',
        location: 'Shenoys Junction Apartment',
        rating: 5,
        text: 'Our 2BHK above MG Road got a full deep clean. The team respected building protocols and finished ahead of schedule.',
        date: 'September 2025',
      },
      {
        name: 'Rajan Abraham',
        location: 'Pallimukku Office',
        rating: 5,
        text: 'Professional crew with NDAs in place. They sanitise our meeting rooms every weekend.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Keep Your M.G. Road Property Business-Ready',
      description:
        'Whether it is a boutique, bank branch, or premium apartment, we deliver hotel-grade cleaning without downtime.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'M.G. Road Kochi',
      introParagraphs: [
        "M.G. Road is Kochi's commercial backbone with constant foot traffic and metro access. Dust, fingerprints, and smudges can ruin first impressions if not handled daily.",
        'Tools & Bubbles supplies discreet, uniformed teams to clean retail spaces, offices, and premium homes along the stretch without interrupting trading hours.',
      ],
      services: {
        lead: 'From display-ready showroom cleaning to luxury apartment deep cleans, we tailor services to each M.G. Road address.',
        bullets: [
          'Showroom floor polishing, glass cleaning, and fitting room sanitisation',
          'Daily or weekly office housekeeping for corporate floors along the metro',
          'Deep cleaning for high-rise residences near Menaka and Ravipuram',
          'Pantry, restroom, and lobby maintenance for commercial towers',
          'Marble, granite, and vitrified tile buffing for premium spaces',
          'Airbnb turnover cleaning for short-stay apartments on M.G. Road',
        ],
      },
      lifestyle: [
        'Retail tenants and banks often need cleaning before or after business hours. Our crews operate 6 AM to midnight with prior scheduling.',
        'Premium apartments above retail stores require noise-free cleaning. We use low-noise machines and coordinate with building management.',
      ],
      coverage: [
        'We serve the entire stretch from Jos Junction to Menaka, including connecting roads like Banerji Road, D.H. Road, and SA Road.',
      ],
      sameDay: [
        'Emergency clean-up before a VIP visit? Same-day service is possible when booked before noon and confirmed by phone.',
      ],
      booking: [
        'Corporate clients can sign up for maintenance contracts. Individuals can book online with instant confirmations and digital invoices.',
      ],
      conclusion:
        'Deliver a flawless experience on M.G. Road every day. Connect with our team at +91 9995216660 or reserve your slot online.',
    }),
    faq: createFaq({
      locationName: 'M.G. Road',
      responseTime:
        'Daily cleaning schedules can start within 24 hours. For urgent one-time jobs, call us for same-day availability.',
      areasMention: ['Menaka', 'Jos Junction', 'Shenoys', 'Pallimukku'],
      pricingDetail:
        'Retail and office spaces are quoted after a quick walkthrough or shared floor plan. Apartments follow published pricing on our website.',
      bookingDetail:
        'Use the online form for homes. Businesses can email layouts for a proposal or set up a quick site visit.',
      preparationDetail:
        'Secure display items and cash drawers. For offices, let security know our crew details to avoid access delays.',
      extraQuestion: {
        question: 'Do you work overnight?',
        answer:
          'Yes, overnight cleaning is available for showrooms and banks with supervisor oversight and prior approval.',
      },
    }),
  },

  'panampilly-nagar': {
    slug: 'cleaning-service-panampilly-nagar',
    locationName: 'Panampilly Nagar',
    heroImage: '/images/hero-home.png',
    hero: {
      eyebrow: 'Serving Panampilly Nagar, Atlantis & Giri Nagar',
      title: 'Cleaning Service in Panampilly Nagar',
      description:
        "Premium cleaning for designer apartments, boutique offices, and cafes in Panampilly Nagar's tree-lined avenues.",
      highlights: [
        { icon: 'Sparkles', text: 'Boutique finish' },
        { icon: 'Sun', text: 'Eco-friendly products' },
        { icon: 'Clock', text: 'Flexible slots' },
      ],
      bookNowLocation: 'panampilly-nagar',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'UtensilsCrossed',
        title: 'Cafe & Salon Specialists',
        description:
          "We keep Panampilly's cafes and salons spotless with hygiene-focused daily and weekly cleaning routines.",
      },
      {
        icon: 'HeartHandshake',
        title: 'Concierge-Style Service',
        description:
          'Dedicated relationship managers coordinate keys, access cards, and concierge drop-offs for busy residents.',
      },
      {
        icon: 'Shield',
        title: 'Trusted by Gated Communities',
        description:
          'Our crew is approved by multiple Panampilly associations, ensuring seamless entry and respectful service.',
      },
    ],
    serviceAreas: [
      'Panampilly Nagar Avenues',
      'Ravipuram Extension',
      'Atlantis Junction',
      'Giri Nagar',
      'Thevara Junction',
      'Jawahar Nagar',
      'GCDA Complex',
      'Kochaithoppu Road',
      'Panampilly Nagar High-Rises',
      'Manorama Junction',
      'KC Abraham Master Road',
      'Kadavanthra Bridge Vicinity',
    ],
    serviceAreaCallout: {
      emoji: '🌿',
      title: "Love Panampilly's green vibes?",
      description:
        'Pair it with a spotless home. Ask about our plant-safe, pet-safe cleaning solutions for premium interiors.',
    },
    reviews: [
      {
        name: 'Divya Pradeep',
        location: 'Panampilly Nagar Avenue 7',
        rating: 5,
        text: 'Immaculate attention to detail. They handled our designer furniture with care and left zero chemical smell.',
        date: 'October 2025',
      },
      {
        name: 'Farhan Siddique',
        location: 'Atlantis Junction Cafe',
        rating: 5,
        text: 'Our cafe now follows a strict hygiene protocol thanks to their daily cleaning crew.',
        date: 'September 2025',
      },
      {
        name: 'Ritika Sharma',
        location: 'Panampilly Nagar High-Rise',
        rating: 5,
        text: 'Booked a surprise cleaning for my parents. The concierge coordination was smooth and professional.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Experience Boutique-Level Cleaning in Panampilly Nagar',
      description: 'Book a single session or set a recurring schedule that matches your premium lifestyle.',
      phoneText: 'Call Our Concierge Desk',
    },
    seo: createSeoContent({
      locationName: 'Panampilly Nagar',
      introParagraphs: [
        "Panampilly Nagar is Kochi's most fashionable neighbourhood with boutique homes, cafes, and studios. A premium space deserves premium cleaning.",
        'Our Panampilly crew arrives in uniform, uses plant-safe products, and respects delicate interiors, marble floors, and designer kitchens.',
      ],
      services: {
        lead: 'Select exclusive add-ons like stainless steel polishing, art-safe dusting, and terrace upkeep to complement your home or business.',
        bullets: [
          'Luxury apartment deep cleaning for avenues 1 through 12',
          'Sofa, carpet, and curtain spa for designer living rooms',
          'Salon, spa, and cafe hygiene maintenance around Atlantis',
          'Kitchen and wine cellar detailing for gourmet homes',
          'Balcony, deck, and terrace cleaning overlooking green lanes',
          'Move-in cleaning for high-rise projects near Manorama Junction',
        ],
      },
      lifestyle: [
        'Many Panampilly residents travel frequently. We coordinate key pick-up/drop-off with concierge desks or trusted neighbours.',
        'Eco-friendly solutions keep your indoor plants, pets, and artworks safe while we clean.',
      ],
      coverage: [
        'Our coverage includes Giri Nagar, Ravipuram, GCDA complex, and every gated community connected to Panampilly Nagar.',
      ],
      sameDay: ['Hosting an impromptu brunch? Same-day clean-ups are available with minimum 3 hours notice.'],
      booking: [
        'Reserve slots online or request a recurring concierge plan with key management, scheduled reminders, and after-service photos.',
      ],
      conclusion:
        'Keep your Panampilly Nagar address picture-perfect. Book now or call +91 9995216660 for a tailored package.',
    }),
    faq: createFaq({
      locationName: 'Panampilly Nagar',
      responseTime:
        'Same-day service is available most weekdays. For weekend brunch clean-ups, book a day in advance.',
      areasMention: ['Atlantis', 'Giri Nagar', 'Jawahar Nagar', 'KC Abraham Master Road'],
      pricingDetail:
        'Premium interiors may need additional detailing. We share exact costs after understanding materials like marble, leather, or hardwood.',
      bookingDetail:
        'Use our website or call the concierge desk to secure a recurring plan with reminders and photo reports.',
      preparationDetail:
        'Secure luxury decor or artwork if necessary. We can also collaborate with your interior designer for specific instructions.',
      extraQuestion: {
        question: 'Do you use eco-friendly chemicals?',
        answer:
          'Yes, we default to plant-safe and pet-safe solutions in Panampilly Nagar unless you request otherwise.',
      },
      extraQuestions: [
        {
          question: 'Do you offer villa deep cleaning in Panampilly Nagar?',
          answer:
            'Yes. We deep-clean designer apartments and villas across Panampilly Nagar avenues, Atlantis, and Giri Nagar with eco-friendly 3M chemicals and Kärcher equipment. Book online for a same-day or next-day slot.',
        },
      ],
    }),
  },

  kadavanthra: {
    slug: 'cleaning-service-kadavanthra',
    locationName: 'Kadavanthra',
    heroImage: '/images/hero-home.png',
    hero: {
      eyebrow: 'Serving SA Road, Jawahar Nagar & GCDA',
      title: 'Cleaning Service in Kadavanthra',
      description:
        'Reliable deep cleaning for apartments, villas, and offices across Kadavanthra, SA Road, and Kadavanthra Junction.',
      highlights: [
        { icon: 'Clock', text: 'Rapid 24h scheduling' },
        { icon: 'Train', text: 'Kadavanthra Metro nearby' },
        { icon: 'Shield', text: 'Verified professionals' },
      ],
      bookNowLocation: 'kadavanthra',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Users',
        title: 'Families & Offices',
        description:
          'We manage both family apartments and commercial offices on SA Road with separate teams and SOPs.',
      },
      {
        icon: 'Sparkles',
        title: 'Move-In Specialists',
        description:
          "Kadavanthra's new towers demand spotless handovers. We deliver builder-level clean-ups ready for inspection.",
      },
      {
        icon: 'Calendar',
        title: 'Weekend Friendly',
        description: 'Saturday and Sunday slots ensure you never need to take leave from work.',
      },
    ],
    serviceAreas: [
      'Kadavanthra Junction',
      'SA Road',
      'Jawahar Nagar',
      'GCDA Colony',
      'Panampilly Extension',
      'Elamkulam',
      'Giri Nagar',
      'Chilavannoor Road',
      'Thevara Ferry Road',
      'Kadavanthra Metro Corridor',
      'Janatha Junction',
      'Ponnurunni',
    ],
    serviceAreaCallout: {
      emoji: '🚇',
      title: 'Kadavanthra Metro commuter?',
      description: 'Book while you travel. We will finish cleaning before you reach home from Kochi Metro.',
    },
    reviews: [
      {
        name: 'Gayathri Narayanan',
        location: 'Kadavanthra SA Road',
        rating: 5,
        text: 'Team arrived sharp at 7 AM and finished before noon. Kitchen and bathrooms look new again.',
        date: 'October 2025',
      },
      {
        name: 'Vinod Kumar',
        location: 'Jawahar Nagar',
        rating: 5,
        text: 'We booked a move-in cleaning. Supervisor ensured every cabinet and light fitting was spotless.',
        date: 'September 2025',
      },
      {
        name: 'Sufia Rahman',
        location: 'Kadavanthra Junction Office',
        rating: 5,
        text: 'Great experience for our accounting firm. Desks and equipment were handled carefully.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: "Book Kadavanthra's Trusted Cleaning Professionals",
      description:
        'Apartments, villas, clubs, or offices – we have a checklist ready for every Kadavanthra space.',
      phoneText: 'Speak With Us',
    },
    seo: createSeoContent({
      locationName: 'Kadavanthra',
      introParagraphs: [
        "Kadavanthra connects Kochi's residential and commercial hubs via SA Road and the metro. Busy households and offices depend on professional cleaning to stay organised.",
        'With dedicated teams stationed near Kadavanthra Junction, Tools & Bubbles delivers punctual, supervised service for homes and businesses alike.',
      ],
      services: {
        lead: 'Mix deep cleaning, sofa shampooing, and office maintenance into one booking tailored to your property.',
        bullets: [
          'Home deep cleaning for SA Road apartments and villas',
          'Move-in/move-out cleaning for new towers in Jawahar Nagar',
          'Office housekeeping for firms near Kadavanthra Junction',
          'Bathroom descaling and stain removal for older houses in GCDA colony',
          'Sofa, carpet, and curtain cleaning for homes in Elamkulam and Giri Nagar',
          'Water tank cleaning for communities along Chilavannoor Road',
        ],
      },
      lifestyle: [
        'Kadavanthra residents juggle metro commutes, business meetings, and family events. Outsourcing cleaning saves precious weekends.',
        'We coordinate directly with building security and handle lift bookings, ensuring zero stress for homeowners.',
      ],
      coverage: [
        'From Ponnurunni to Thevara ferry, our route planning software keeps crews close to every Kadavanthra neighbourhood.',
      ],
      sameDay: [
        'Need service right away? Morning bookings confirmed before 10 AM often qualify for same-day visits.',
      ],
      booking: [
        'Reserve your slot online. Get automated reminders, WhatsApp crew tracking, and photo proof when the service ends.',
      ],
      conclusion:
        'Keep your Kadavanthra property in top shape year-round. Book now or call +91 9995216660 for a personalised quote.',
    }),
    faq: createFaq({
      locationName: 'Kadavanthra',
      responseTime:
        'We operate teams nearby, so 24-hour turnaround is standard. Same-day slots depend on availability.',
      areasMention: ['SA Road', 'Jawahar Nagar', 'GCDA Colony', 'Elamkulam'],
      pricingDetail:
        'Check live pricing on our booking form. Villas or large offices receive custom quotes after a quick walkthrough.',
      bookingDetail:
        'The quickest method is online booking. You will get SMS/WhatsApp confirmations and crew details instantly.',
      preparationDetail:
        'Share intercom numbers or access cards if your building requires them. We handle elevator bookings when needed.',
      extraQuestion: {
        question: 'Can you handle combination bookings for home and office?',
        answer:
          'Yes, schedule back-to-back slots and we will deploy separate residential and commercial teams.',
      },
    }),
  },

  vyttila: {
    slug: 'cleaning-service-vyttila',
    locationName: 'Vyttila',
    heroImage: '/images/hero-apartment.png',
    hero: {
      eyebrow: 'Serving Vyttila Mobility Hub, Pipeline Road & NH Bypass',
      title: 'Cleaning Service in Vyttila',
      description:
        'Dependable cleaning for apartments, villas, and commercial spaces around Vyttila Junction and Mobility Hub.',
      highlights: [
        { icon: 'Bus', text: 'Mobility hub coverage' },
        { icon: 'Clock', text: 'Same-day arrival' },
        { icon: 'CheckCircle', text: 'Guaranteed satisfaction' },
      ],
      bookNowLocation: 'vyttila',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Train',
        title: 'Multi-Modal Friendly',
        description:
          'Vyttila residents switch between metro, bus, and water metro. We coordinate cleaning around your commute.',
      },
      {
        icon: 'Sparkles',
        title: 'Busy Junction Specialists',
        description:
          'Our teams know how to navigate Vyttila traffic and reach your home on time, every time.',
      },
      {
        icon: 'Shield',
        title: 'Background Verified Crew',
        description:
          'Every professional is trained, insured, and supervisor monitored for a worry-free experience.',
      },
    ],
    serviceAreas: [
      'Vyttila Mobility Hub',
      'Pipeline Road',
      'Chilavannoor',
      'Ponnurunni',
      'NH Bypass Residences',
      'Kaniampuzha Road',
      'Tripunithura Road',
      'Vyttila Temple Junction',
      'Toll Junction',
      'Petta',
      'Power House Road',
      'Vyttila Water Metro Vicinity',
    ],
    serviceAreaCallout: {
      emoji: '🚤',
      title: 'Close to Vyttila Mobility Hub?',
      description: 'Book a slot while you commute. We send updates so you can approve service remotely.',
    },
    reviews: [
      {
        name: 'Sneha Varghese',
        location: 'Pipeline Road',
        rating: 5,
        text: 'Impressed with their punctuality despite heavy traffic. Kitchen deep cleaning was outstanding.',
        date: 'October 2025',
      },
      {
        name: 'John Joseph',
        location: 'NH Bypass Apartment',
        rating: 5,
        text: 'They handled sofa cleaning and bathroom descaling in one visit. Crew was polite and efficient.',
        date: 'September 2025',
      },
      {
        name: 'Roshini Francis',
        location: 'Chilavannoor Villas',
        rating: 5,
        text: 'Booked before a family function. House smelled fresh and sparkling clean for guests.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Beat the Vyttila Rush with Pro Cleaning',
      description: 'Lock your preferred slot today and return to a spotless home after your commute.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'Vyttila',
      introParagraphs: [
        "Vyttila Junction is Kerala's busiest transport hub with bus, metro, and water metro connections. Homes nearby gather dust and grime faster than quieter suburbs.",
        'Our Vyttila crew is trained to work around tight schedules, parking constraints, and apartment association rules.',
      ],
      services: {
        lead: 'Whether you live in a NH Bypass high-rise or a riverside villa in Chilavannoor, we have a cleaning plan built for you.',
        bullets: [
          'Deep cleaning for apartments near Vyttila Mobility Hub and Toll Junction',
          'Bathroom and kitchen detailing for villas along Chilavannoor backwaters',
          'Sofa, carpet, and mattress cleaning for homes on Pipeline Road',
          'Commercial cleaning for offices around Ponnurunni and Kaniampuzha Road',
          'Water tank and sump cleaning for high-density apartment complexes',
          'Move-in cleanups for new towers along the NH Bypass corridor',
        ],
      },
      lifestyle: [
        'With hectic commutes, Vyttila families prefer early morning or late evening slots. Our teams operate from 6 AM to 9 PM daily.',
        'We provide remote approvals via WhatsApp so you can monitor progress even while travelling.',
      ],
      coverage: [
        'We serve Power House Road, Tripunithura Road, and Petta as part of the Vyttila cluster, covering every lane around the junction.',
      ],
      sameDay: [
        'Same-day cleanups are common for Vyttila residents. Book before noon for best availability.',
      ],
      booking: [
        'Reserve online or call to customise packages. Expect reminders, crew tracking, and completion photos for peace of mind.',
      ],
      conclusion:
        'Keep your Vyttila property guest-ready despite the city rush. Book online or call +91 9995216660 today.',
    }),
    faq: createFaq({
      locationName: 'Vyttila',
      responseTime:
        'Same-day service is common thanks to our hub near the junction. Weekend slots fill quickly, so book early.',
      areasMention: ['Pipeline Road', 'Chilavannoor', 'Ponnurunni', 'NH Bypass'],
      pricingDetail:
        'Prices are listed online. Villas or duplexes may need a quick assessment call to confirm exact totals.',
      bookingDetail:
        'Use Book Now for instant confirmation or call us if you prefer to coordinate around school runs and commutes.',
      preparationDetail:
        'Share parking info near the junction to help our crew avoid towing. Lift reservations can also be handled in advance.',
      extraQuestions: [
        {
          question: 'Can your crew reach Vyttila Mobility Hub apartments the same day?',
          answer:
            'Yes. Teams stationed near Vyttila Junction regularly take same-day deep cleans for Mobility Hub and NH Bypass apartments when you book before noon.',
        },
      ],
    }),
  },

  palarivattom: {
    slug: 'cleaning-service-palarivattom',
    locationName: 'Palarivattom',
    heroImage: '/images/hero-apartment.png',
    hero: {
      eyebrow: 'Serving Palarivattom Signal, Pipeline Road & Kaloor Link',
      title: 'Cleaning Service in Palarivattom',
      description:
        'Professional cleaning for apartments and offices around Palarivattom Signal, Pipeline Road, and Edappally Link Road.',
      highlights: [
        { icon: 'Clock', text: 'On-time arrival guaranteed' },
        { icon: 'Train', text: 'Metro-friendly scheduling' },
        { icon: 'Sparkles', text: 'Zero dust left behind' },
      ],
      bookNowLocation: 'palarivattom',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Building',
        title: 'High-Rise Experts',
        description:
          'We serve the high-density apartment clusters near Palarivattom Signal with specialised lift-friendly equipment.',
      },
      {
        icon: 'Clock',
        title: 'Traffic-Smart Teams',
        description:
          'Crews depart at off-peak hours to reach your building on schedule despite signal congestion.',
      },
      {
        icon: 'Shield',
        title: 'Verified & Insured',
        description: 'All staff are background checked and insured, giving families total peace of mind.',
      },
    ],
    serviceAreas: [
      'Palarivattom Signal',
      'Pipeline Road',
      'Edappally Link Road',
      'Ponekkara',
      'Kaloor Link Road',
      'Thammanam',
      'Civil Line Road',
      'Changampuzha Nagar',
      'Vazhakkala',
      'Deshabhimani Extension',
      'Palachuvadu',
      'JLN Stadium Vicinity',
    ],
    serviceAreaCallout: {
      emoji: '🚦',
      title: 'Stuck near Palarivattom Signal?',
      description:
        'Let us clean while you beat the traffic. Remote approvals and updates keep you in control.',
    },
    reviews: [
      {
        name: 'Chithra Babu',
        location: 'Palarivattom Pipeline Road',
        rating: 5,
        text: 'Booked online and the crew reached exactly at 8 AM. They handled our 3BHK brilliantly.',
        date: 'October 2025',
      },
      {
        name: 'Sebastian Paul',
        location: 'Civil Line Road',
        rating: 5,
        text: 'Excellent post-renovation cleaning. Dust-free within hours.',
        date: 'September 2025',
      },
      {
        name: 'Nimisha Varma',
        location: 'Palarivattom Junction Office',
        rating: 5,
        text: 'Our small office stays fresh with their bi-weekly housekeeping plan.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Skip the Signal, Return to a Sparkling Home',
      description:
        "Book your preferred slot today and experience Palarivattom's most reliable cleaning service.",
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'Palarivattom',
      introParagraphs: [
        'Palarivattom connects Edappally, Kaloor, and Vyttila with constant traffic at the main signal. Dust and pollution accumulate quickly in homes and offices nearby.',
        'Our crews station themselves near the junction to reach your apartment or office without delay, even during peak hours.',
      ],
      services: {
        lead: 'Customise your cleaning package with add-ons for sofa shampooing, water tank maintenance, or balcony detailing.',
        bullets: [
          'Full apartment deep cleaning for Pipeline Road and Civil Line residences',
          'Bathroom descaling for older flats near Palarivattom Signal',
          'Kitchen degreasing for food businesses along Edappally Link Road',
          'Sofa, mattress, and carpet cleaning for homes in Changampuzha Nagar',
          'Office housekeeping for agencies around Thammanam and Vazhakkala',
          'Move-in cleaning for new towers connecting to JLN Stadium',
        ],
      },
      lifestyle: [
        'Most Palarivattom residents prefer early morning slots to avoid parking hassles. Our planners accommodate your timing and building guidelines.',
        'We offer remote approval and photo updates so working professionals can manage cleaning while at the office.',
      ],
      coverage: [
        "From Palachuvadu to Kaloor Link Road, we cover every lane feeding into Palarivattom's signal.",
      ],
      sameDay: [
        'Same-day appointments are common when booked before 11 AM. Evening slots fill up quickly, so reserve early.',
      ],
      booking: [
        'Book online in less than a minute. Need help? Call us and our coordinator will arrange the slot with building permissions.',
      ],
      conclusion: 'Let us handle the cleaning while you handle the commute. Book now or call +91 9995216660.',
    }),
    faq: createFaq({
      locationName: 'Palarivattom',
      responseTime: 'Crews based near the junction allow for same-day or next-day scheduling year-round.',
      areasMention: ['Pipeline Road', 'Edappally Link Road', 'Civil Line Road', 'Thammanam'],
      pricingDetail:
        'Use our online estimator. Shared spaces or larger offices get a bespoke quote after a quick call.',
      bookingDetail:
        'Book online or via WhatsApp. We send crew details, OTP entry codes, and completion reports digitally.',
      preparationDetail: 'Tell us about parking or one-way restrictions so the team can plan the best route.',
      extraQuestions: [
        {
          question: 'Do you serve apartments around Palarivattom Signal the same day?',
          answer:
            'Yes. Crews based near the signal cover Pipeline Road, Edappally Link Road, and Civil Line Road. Book before 11 AM for the best same-day availability.',
        },
      ],
    }),
    localLandmarks: [
      'Palarivattom Signal (junction)',
      'Pipeline Road',
      'Edappally Link Road',
      'Kaloor Link Road',
      'JLN Stadium (nearby)',
      'Changampuzha Nagar',
      'Thammanam',
    ],
    pricingTable: [
      { service: 'Deep cleaning (2BHK)', fromAmount: 2999, notes: 'Pipeline Road & Civil Line' },
      { service: 'Deep cleaning (3BHK)', fromAmount: 3999, notes: 'Full home' },
      { service: 'Sofa cleaning', fromAmount: 999, notes: 'Per seat' },
      { service: 'Bathroom deep cleaning', fromAmount: 999, notes: 'Anti-fungal' },
      { service: 'Office cleaning', fromAmount: 4500, notes: 'Bi-weekly plans available' },
    ],
  },

  eloor: {
    slug: 'cleaning-service-eloor',
    locationName: 'Eloor',
    heroImage: '/images/hero-deep-cleaning.png',
    hero: {
      eyebrow: 'Serving Eloor, Udyogamandal & Pathalam Bridge',
      title: 'Cleaning Service in Eloor',
      description:
        'Industrial-strength cleaning for riverfront homes, factories, and offices across Eloor and Udyogamandal.',
      highlights: [
        { icon: 'Anchor', text: 'Industrial expertise' },
        { icon: 'Shield', text: 'Safety compliant' },
        { icon: 'Clock', text: 'Quick deployment' },
      ],
      bookNowLocation: 'eloor',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Shield',
        title: 'Safety-First Teams',
        description:
          'Our technicians follow PPE protocols suited for industrial townships and chemical plants.',
      },
      {
        icon: 'Sparkles',
        title: 'Riverfront Care',
        description:
          'Homes near Periyar River need specialised mould and humidity control—we bring the right equipment.',
      },
      {
        icon: 'Briefcase',
        title: 'Factory & Office Friendly',
        description:
          'Shift-based cleaning for plants, warehouses, and admin blocks with supervisor oversight.',
      },
    ],
    serviceAreas: [
      'Eloor Municipal Area',
      'Udyogamandal',
      'FACT Township',
      'Pathalam Bridge',
      'Eloor Ferry',
      'Cheranelloor',
      'Varappuzha',
      'Eloor North',
      'Kadungalloor',
      'Manjummel',
      'FACT School Zone',
      'Eloor Industrial Estate',
    ],
    serviceAreaCallout: {
      emoji: '🏭',
      title: 'Industrial site needing housekeeping?',
      description: 'Set up recurring shifts with safety-trained staff and chemical-resistant equipment.',
    },
    reviews: [
      {
        name: 'Vishnu Mohan',
        location: 'FACT Township',
        rating: 5,
        text: 'They deep cleaned our quarters and handled hard water stains perfectly. Zero hassle with gate passes.',
        date: 'October 2025',
      },
      {
        name: 'Soumya Suresh',
        location: 'Eloor Ferry Road',
        rating: 5,
        text: 'Professional crew that understood river humidity issues. Mould treatment worked great.',
        date: 'September 2025',
      },
      {
        name: 'Manoj Kumar',
        location: 'Udyogamandal Plant Office',
        rating: 5,
        text: 'Our administrative block looks spotless every Monday after their weekend crew finishes.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Industrial-Grade Cleaning for Eloor & Udyogamandal',
      description:
        'Book a one-time overhaul or set up routine maintenance that keeps staff housing and offices hygienic.',
      phoneText: 'Schedule a Site Visit',
    },
    seo: createSeoContent({
      locationName: 'Eloor',
      introParagraphs: [
        "Eloor's blend of factories, townships, and riverfront homes demands robust cleaning solutions. Regular housekeeping rarely handles chemical dust or river humidity.",
        'Tools & Bubbles maintains a dedicated Eloor team trained in PPE protocols, safety briefings, and compliance documentation for industrial estates.',
      ],
      services: {
        lead: 'Combine residential deep cleaning, hostel sanitation, and industrial housekeeping under a single trusted vendor.',
        bullets: [
          'Deep cleaning for FACT and Udyogamandal staff quarters',
          'Office housekeeping for plant admin blocks and meeting rooms',
          'Mould and odour control for river-facing homes in Eloor',
          'Tank, sump, and drainage cleaning for industrial facilities',
          'Machinery bay sweeping and safety zone cleaning',
          'Post-shutdown cleaning for maintenance shutdowns',
        ],
      },
      lifestyle: [
        'Shift workers need flexible housekeeping. Our crews operate early mornings, late nights, and weekends to fit plant schedules.',
        'River humidity accelerates mould growth. We use industrial dehumidifiers and safe anti-fungal treatments to protect interiors.',
      ],
      coverage: [
        'We serve Eloor North, FACT townships, Cheranelloor, and Varappuzha with equal efficiency, coordinating gate passes in advance.',
      ],
      sameDay: [
        'Urgent cleanups before audits or VIP visits can be arranged the same day with supervisor approval.',
      ],
      booking: [
        'Book online for residential services or request a customised proposal for factories and warehouses. Compliance certificates provided on request.',
      ],
      conclusion:
        "Partner with a cleaning team that understands Eloor's unique requirements. Call +91 9995216660 or schedule online.",
    }),
    faq: createFaq({
      locationName: 'Eloor',
      responseTime:
        'Residential bookings are served within 24 hours. Industrial cleanups are scheduled after a quick assessment.',
      areasMention: ['FACT Township', 'Udyogamandal', 'Pathalam', 'Cheranelloor'],
      pricingDetail:
        'Residential pricing is available online. Industrial and hostel projects receive a custom quotation after understanding scope and safety needs.',
      bookingDetail:
        'Share plant entry rules, PPE requirements, and preferred shift timings. We will align crew deployment accordingly.',
      preparationDetail:
        'Provide gate pass details and ensure key contacts are available for safety briefings.',
      extraQuestion: {
        question: 'Do you provide MSDS and compliance certificates?',
        answer:
          'Yes, we share chemical MSDS sheets and issue compliance certificates after each service when required.',
      },
    }),
  },

  'willingdon-island': {
    slug: 'cleaning-service-willingdon-island',
    locationName: 'Willingdon Island',
    heroImage: '/images/hero-kochi-city.png',
    hero: {
      eyebrow: 'Serving Port Trust, Naval Base & Luxury Hotels',
      title: 'Cleaning Service in Willingdon Island',
      description:
        'Marine-grade cleaning for port offices, naval quarters, and luxury hotels across Willingdon Island.',
      highlights: [
        { icon: 'Anchor', text: 'Port & naval compliant' },
        { icon: 'Sparkles', text: 'Hospitality standard finish' },
        { icon: 'Clock', text: 'Coordinated shift timing' },
      ],
      bookNowLocation: 'willingdon-island',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Briefcase',
        title: 'Marine & Hospitality Expertise',
        description:
          'We serve shipping offices, warehouses, and five-star hotels with specialised marine-safe techniques.',
      },
      {
        icon: 'Shield',
        title: 'Security-Cleared Crew',
        description: 'All staff undergo background checks suitable for naval base and port trust access.',
      },
      {
        icon: 'Calendar',
        title: 'Shift-Friendly Scheduling',
        description:
          'We align with ship schedules, check-in timings, and banquet events without disrupting operations.',
      },
    ],
    serviceAreas: [
      'Cochin Port Trust',
      'Naval Base Housing',
      'Mattancherry Wharf',
      'Ernakulam Wharf',
      'Container Terminal Offices',
      'Hotel Zone',
      'Old Harbour Road',
      'Vallarpadam Access',
      "Xavier's Island Road",
      'Embassy & Consulate Buildings',
      'Ferry Linking Terminals',
      'Cruise Berth Facilities',
    ],
    serviceAreaCallout: {
      emoji: '🛳️',
      title: 'Managing a ship call or VIP visit?',
      description:
        'Deploy special cleaning squads for cabins, terminals, and banquets aligned to your docking schedule.',
    },
    reviews: [
      {
        name: 'Commodore S. Pillai',
        location: 'Naval Base Housing',
        rating: 5,
        text: 'Professional and discreet. They followed all security protocols and left quarters spotless.',
        date: 'October 2025',
      },
      {
        name: 'Manoj Varrier',
        location: 'Luxury Hotel, Willingdon Island',
        rating: 5,
        text: 'Our banquet hall turnaround time improved thanks to their night crew. Excellent detail.',
        date: 'September 2025',
      },
      {
        name: 'Leena George',
        location: 'Port Trust Office',
        rating: 5,
        text: 'Meeting rooms and cabins receive weekly maintenance. Crew is punctual and well-trained.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Marine-Grade Cleaning for Willingdon Island',
      description: 'Book flexible schedules matched to ship calls, hotel check-ins, and naval protocols.',
      phoneText: 'Call Our Operations Desk',
    },
    seo: createSeoContent({
      locationName: 'Willingdon Island',
      introParagraphs: [
        'Willingdon Island houses ports, hotels, naval facilities, and warehouses. Each requires meticulous cleaning that respects safety and security guidelines.',
        'Our port-trained teams arrive with ID verification, PPE, and marine-safe chemicals that protect steel, teak, and brass surfaces.',
      ],
      services: {
        lead: 'Choose dedicated crews for hotels, ships, terminals, and residences with custom checklists for each property.',
        bullets: [
          'Cabin and crew accommodation cleaning between ship calls',
          'Banquet and ballroom turnaround for luxury hotels',
          'Office and terminal maintenance for Cochin Port Trust',
          'Naval housing deep cleaning with confidentiality assured',
          'Warehouse sweeping and sanitisation for container yards',
          'VIP lounge and cruise terminal detailing before arrivals',
        ],
      },
      lifestyle: [
        'Shift-based operations demand 24/7 availability. We coordinate night and early-morning cleans tied to docking schedules.',
        'Security clearance and restricted chemical usage are standard for our Willingdon Island teams.',
      ],
      coverage: [
        'We cover Old Harbour Road, Mattancherry Wharf, cruise berths, and connecting ferry terminals with equal precision.',
      ],
      sameDay: [
        'Emergency cleanups before inspection or VIP visits can be arranged with minimal notice. Dedicated supervisors ensure compliance.',
      ],
      booking: [
        'Contact our operations desk or submit an online enquiry with docking schedules. We prepare SOPs and crew lists for approval.',
      ],
      conclusion:
        'Deliver a five-star first impression on Willingdon Island. Book now or call +91 9995216660.',
    }),
    faq: createFaq({
      locationName: 'Willingdon Island',
      responseTime:
        'We align with ship schedules. For urgent requirements, call us and we will mobilise within hours.',
      areasMention: ['Port Trust', 'Naval Base', 'Hotel Zone', 'Mattancherry Wharf'],
      pricingDetail:
        'Pricing depends on scale, security protocols, and timing. We provide clear proposals with scope, staffing, and compliance details.',
      bookingDetail: 'Share your ship call or event calendar. We will align crews, PPE, and passes to match.',
      preparationDetail:
        'Ensure security passes and escort approvals are arranged. We supply crew IDs and vehicle details beforehand.',
      extraQuestion: {
        question: 'Do you supply manpower for long contracts?',
        answer:
          'Yes, we provide long-term housekeeping teams for hotels, terminals, and naval housing with on-site supervisors.',
      },
    }),
  },

  aluva: {
    slug: 'cleaning-service-aluva',
    locationName: 'Aluva',
    heroImage: '/images/hero-aluva.png',
    hero: {
      eyebrow: 'Serving Aluva Metro, Periyar Riverside & Airport Corridor',
      title: 'Cleaning Service in Aluva',
      description:
        'Professional cleaning for villas, apartments, and hostels across Aluva, including airport corridor and Periyar riverside homes.',
      highlights: [
        { icon: 'Train', text: 'Aluva metro & rail access' },
        { icon: 'Waves', text: 'River-safe cleaning' },
        { icon: 'Clock', text: 'Same-day support' },
      ],
      bookNowLocation: 'aluva',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Sparkles',
        title: 'Riverfront Detailing',
        description:
          'Humidity control and anti-mould treatments suited for Periyar-facing villas and apartments.',
      },
      {
        icon: 'Calendar',
        title: 'Airport-Friendly Scheduling',
        description: 'We work around flight schedules for families living near the airport corridor.',
      },
      {
        icon: 'Users',
        title: 'Hostel & PG Expertise',
        description: "Aluva's student population trusts us for hostel deep cleans between academic sessions.",
      },
    ],
    serviceAreas: [
      'Aluva Town',
      'Periyar Riverside',
      'Aluva Metro Station',
      'Airport Seaport Road',
      'Chowara',
      'Desom',
      'Thaikkattukara',
      'Paravur Junction',
      'Uliyannoor',
      'Keezhmad',
      'Choondy',
      'Pulinchode',
    ],
    serviceAreaCallout: {
      emoji: '✈️',
      title: 'Travelling frequently?',
      description: 'Book recurring sessions that keep your home guest-ready even when you are out of town.',
    },
    reviews: [
      {
        name: 'Anupama R',
        location: 'Periyar Riverside Villa',
        rating: 5,
        text: 'Their anti-mould treatment keeps our river-facing home fresh. Highly recommended.',
        date: 'October 2025',
      },
      {
        name: 'Jithin Paul',
        location: 'Aluva Metro Quarter',
        rating: 5,
        text: 'Booked a deep clean before moving back from abroad. Excellent coordination and results.',
        date: 'September 2025',
      },
      {
        name: 'Fathima Noor',
        location: 'Aluva Airport Corridor PG',
        rating: 5,
        text: 'They manage our hostel cleaning every semester break. Rooms are spotless and sanitised.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Keep Your Aluva Home Fresh & Guest-Ready',
      description: 'Book a visit today and enjoy riverside living without the maintenance stress.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'Aluva',
      introParagraphs: [
        'Aluva blends riverfront villas, metro-connected apartments, and airport corridor hostels. Each requires different cleaning strategies to stay fresh.',
        'We base a dedicated crew near Aluva metro so you get same-day service any time of the year.',
      ],
      services: {
        lead: 'Deep cleaning, sofa care, and humidity control solutions keep Aluva homes comfortable despite river moisture.',
        bullets: [
          'Riverfront villa deep cleaning with anti-mould treatment',
          'Hostel and PG sanitisation between academic batches',
          'Sofa, carpet, and curtain spa for families returning from abroad',
          'Kitchen and bathroom detailing for apartments near Aluva metro',
          'Water tank cleaning for suburban layouts in Keezhmad and Choondy',
          'Move-in/move-out cleaning for airport corridor rentals',
        ],
      },
      lifestyle: [
        'Families who travel or work abroad rely on us to maintain homes while away. We send photo proof and maintain checklists so you stay updated.',
        'Students and working professionals appreciate quick hostel cleanups that fit between academic calendars or shift patterns.',
      ],
      coverage: [
        'From Pulinchode to Paravur Junction, our crew covers every Aluva neighbourhood, including localities along Airport Seaport Road.',
      ],
      sameDay: [
        'Most weekday bookings before noon qualify for same-day slots thanks to teams stationed near the metro.',
      ],
      booking: [
        'Reserve via our website or call for custom requests. Expect reminder alerts, crew tracking, and completion photos.',
      ],
      conclusion:
        'Enjoy riverside living without the maintenance stress. Book today or call +91 9995216660 for a tailored quote.',
    }),
    faq: createFaq({
      locationName: 'Aluva',
      responseTime:
        'Same-day visits are common. Weekend and holiday slots fill quickly, so lock your booking early.',
      areasMention: ['Periyar Riverside', 'Airport Seaport Road', 'Keezhmad', 'Thaikkattukara'],
      pricingDetail:
        'Apartment and villa pricing appears on the booking form. Hostels or large villas receive a quick call to confirm customised rates.',
      bookingDetail:
        'Book online, call, or share your travel itinerary and we will align cleaning while you are away.',
      preparationDetail:
        'Keep keys ready with a neighbour or security. Share gate/association instructions so we can coordinate entry smoothly.',
    }),
  },

  broadway: {
    slug: 'cleaning-service-broadway-kochi',
    locationName: 'Broadway & Ernakulam Bazaar',
    heroImage: '/images/hero-mg-road.png',
    hero: {
      eyebrow: 'Serving Broadway Market, Ernakulam Bazaar & Mattancherry Link',
      title: 'Cleaning Service in Broadway Kochi',
      description:
        'Market-ready cleaning for retail shops, godowns, and heritage homes around Broadway, Market Road, and Ernakulam Bazaar.',
      highlights: [
        { icon: 'Sparkles', text: 'Market-ready finish' },
        { icon: 'Clock', text: 'Before-opening slots' },
        { icon: 'Shield', text: 'Trusted by traders' },
      ],
      bookNowLocation: 'broadway',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Briefcase',
        title: 'Retail-Grade Detailing',
        description:
          'Showcase products without dust. We polish showcases, shutters, racks, and billing counters before foot traffic starts.',
      },
      {
        icon: 'Clock',
        title: 'Before & After Market Hours',
        description:
          'Crews operate as early as 5 AM or post 9 PM so your store stays spotless without interrupting sales.',
      },
      {
        icon: 'Shield',
        title: 'Inventory Safe',
        description:
          'Supervisors document before/after photos and ensure inventory, cash drawers, and showcases remain sealed.',
      },
    ],
    serviceAreas: [
      'Broadway Market Lanes',
      'Ernakulam Bazaar',
      'Market Road',
      'Mattancherry Bridge Front',
      'Jew Street & Synagogue Lane',
      'Banerji Road Stretch',
      'TD Road',
      'Penta Menaka Vicinity',
      'Boat Jetty Road',
      'Hospital Road',
      'Chittoor Road Junction',
      'Shanmugham Road Link',
    ],
    serviceAreaCallout: {
      emoji: '🧺',
      title: 'Own a shop inside Broadway?',
      description:
        'Schedule daily or weekly cleaning before shutters open and keep aisles spotless for customers.',
    },
    reviews: [
      {
        name: 'Faizal Hashim',
        location: 'Broadway Textiles',
        rating: 5,
        text: 'Their team reaches by 6 AM and leaves the store sparkling before customers arrive. Shelves and trial rooms stay immaculate.',
        date: 'October 2025',
      },
      {
        name: 'Lakshmi Raj',
        location: 'Jew Street Boutique',
        rating: 5,
        text: 'Gentle with antiques and fabric inventory. We trust them for weekly deep cleaning and window displays.',
        date: 'September 2025',
      },
      {
        name: 'Rohit Dhananjay',
        location: 'Market Road Godown',
        rating: 5,
        text: 'Heavy dust removed from our storage warehouse in a single shift. Professional and thorough.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Keep Your Broadway Store Customer-Ready',
      description:
        'Daily wipe downs or full deep cleans—we craft a schedule that keeps your retail space shining.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'Broadway Kochi',
      introParagraphs: [
        'Broadway and Ernakulam Bazaar welcome thousands of shoppers every day. Dust, shoe marks, and humidity can dull your display if not cleaned daily.',
        'Tools & Bubbles supports traders with pre-opening and post-closing cleaning routines that keep stores, godowns, and offices spotless.',
      ],
      services: {
        lead: 'Combine shop front detailing, stock room cleaning, and office upkeep into one reliable contract.',
        bullets: [
          'Daily sweeping, mopping, and sanitising for retail outlets',
          'Glass and showcase polishing for jewellers and boutiques',
          'Godown deep cleaning and shelving dust removal',
          'Pantry, staff room, and restroom maintenance',
          'Post-renovation cleanups for remodelled stores',
          'Heritage home cleaning for residences around Market Road',
        ],
      },
      lifestyle: [
        'Broadway traders open early. Our crews arrive before shutters lift, ensuring floors, displays, and billing counters shine when customers walk in.',
        'Busy festival seasons demand quick turnaround. We scale manpower during Vishu, Onam, and Christmas rush.',
      ],
      coverage: [
        'We cover Market Road to Mattancherry Bridge, including Synagogue Lane, Banerji Road, and Shanmugham Road storefronts.',
      ],
      sameDay: [
        'Need an emergency cleanup after late-night stocking? Same-day slots are available with supervisor approval.',
      ],
      booking: [
        'Book recurring schedules online or call our retail specialist. Receive digital checklists, attendance logs, and photo proof after every visit.',
      ],
      conclusion:
        "Let customers focus on your products—not the dust. Book Broadway's trusted cleaning crew or call +91 9995216660.",
    }),
    faq: createFaq({
      locationName: 'Broadway Kochi',
      responseTime: 'We can start within 24 hours. Existing clients enjoy fixed daily or weekly slots.',
      areasMention: ['Ernakulam Bazaar', 'Market Road', 'Mattancherry Bridge', 'Jew Street'],
      pricingDetail:
        'Small shops can opt for daily wipe-down packages, while larger godowns receive customised quotes based on square footage and frequency.',
      bookingDetail:
        'Schedule via phone or online. We map cleaning before store opening or after closing to avoid downtime.',
      preparationDetail:
        'Secure cash drawers and fragile inventory. Provide access to water points and nominate a staff contact for coordination.',
      extraQuestion: {
        question: 'Do you handle festival season deep cleans?',
        answer:
          'Yes, we add extra manpower and night crews during peak seasons so your store remains spotless throughout.',
      },
    }),
  },

  'marine-drive': {
    slug: 'cleaning-service-marine-drive-kochi',
    locationName: 'Marine Drive Kochi',
    heroImage: '/images/hero-marine-drive.png',
    hero: {
      eyebrow: 'Serving Marine Drive Promenade, Shanmugham Road & GCDA Towers',
      title: 'Cleaning Service in Marine Drive Kochi',
      description:
        'Waterfront cleaning specialists for high-rise apartments, premium offices, and cafes along Marine Drive and Shanmugham Road.',
      highlights: [
        { icon: 'Waves', text: 'Waterfront safe methods' },
        { icon: 'Sparkles', text: 'Glass & facade experts' },
        { icon: 'Clock', text: 'Flexible scheduling' },
      ],
      bookNowLocation: 'marine-drive',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Sparkles',
        title: 'Glass & Balcony Perfection',
        description:
          'Salt-laden breeze leaves marks quickly. We polish glass, railings, and decks to keep your view pristine.',
      },
      {
        icon: 'Shield',
        title: 'High-Rise Ready Crew',
        description:
          'Our team handles tower access, service elevators, and building protocols without disturbing neighbours.',
      },
      {
        icon: 'Calendar',
        title: 'Sunrise & Sunset Slots',
        description: 'Schedule early morning or late evening cleans to avoid peak promenade traffic.',
      },
    ],
    serviceAreas: [
      'Marine Drive Promenade',
      'GCDA Apartment Complex',
      'Bay Pride Mall Vicinity',
      'Shanmugham Road High-Rises',
      'Atlantis Walkway Residences',
      'Rainbow Bridge Apartments',
      'Kundanoor Link Towers',
      'Boat Jetty Front Offices',
      'High Court Junction',
      "Queen's Way",
      'Foreshore Road Cafes',
      'Bolgatty View Residences',
    ],
    serviceAreaCallout: {
      emoji: '🌅',
      title: 'Love the Marine Drive sunset?',
      description: 'Let us keep your balcony, windows, and deck ready for every golden hour.',
    },
    reviews: [
      {
        name: 'Rakesh Pillai',
        location: 'GCDA Towers',
        rating: 5,
        text: 'Glass cleaning was spotless. They worked around condo rules and left our apartment ready for a family event.',
        date: 'October 2025',
      },
      {
        name: 'Maria Dsouza',
        location: 'Marine Drive Cafe',
        rating: 5,
        text: 'Tables, balustrades, and facade shine every morning. Perfect for waterfront dining.',
        date: 'September 2025',
      },
      {
        name: 'Anand Varma',
        location: 'Shanmugham Road Office',
        rating: 5,
        text: 'Professional crew that respects office hours. Floors and windows look brand new.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Keep Your Marine Drive View Crystal Clear',
      description: 'Book waterfront-safe cleaning and enjoy spotless interiors without the salt and dust.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'Marine Drive Kochi',
      introParagraphs: [
        "Marine Drive's sea breeze brings beauty and salt deposits in equal measure. Routine professional cleaning keeps balconies, facades, and interiors shining.",
        'Our waterfront-trained crew uses corrosion-safe products and high-reach tools to maintain high-rise apartments, offices, and cafes along Shanmugham Road.',
      ],
      services: {
        lead: 'Choose from balcony refresh sessions, glass facade cleaning, and full apartment deep cleaning designed for waterfront living.',
        bullets: [
          'Balcony, deck, and railing detailing with salt guard treatment',
          'Glass and window cleaning for towers along Marine Drive',
          'Full apartment deep cleaning with upholstery and mattress care',
          'Office and cafe maintenance along promenade walkways',
          'Marble and wooden floor polishing for premium interiors',
          'Water tank and sump cleaning for high-rise complexes',
        ],
      },
      lifestyle: [
        'Residents prefer sunrise or late evening slots to keep views uninterrupted. We coordinate building permissions and service elevator bookings.',
        'Cafes and lounges opt for pre-opening cleans so customers experience a spotless waterfront ambience.',
      ],
      coverage: [
        'We service the entire Marine Drive stretch from High Court Junction to Atlantis, including GCDA towers and Foreshore Road properties.',
      ],
      sameDay: [
        'Need urgent cleaning before guests arrive? Same-day assistance is often available with a quick call.',
      ],
      booking: [
        'Book online and receive reminders, crew identification, and completion photos. Long-term residents can set monthly or quarterly schedules.',
      ],
      conclusion:
        'Protect your Marine Drive investment from salt, dust, and humidity. Reserve a slot or call +91 9995216660 now.',
    }),
    faq: createFaq({
      locationName: 'Marine Drive Kochi',
      responseTime:
        'We typically respond within 24 hours. For window cleaning, we schedule at least a day ahead to arrange equipment.',
      areasMention: ['GCDA Towers', 'Shanmugham Road', 'Bay Pride Mall', 'Foreshore Road'],
      pricingDetail:
        'Balcony refresh packages start from our published rates. Facade and high-rise window cleaning are quoted after assessing height and access.',
      bookingDetail:
        'Use our website or contact our concierge line. Share building security protocols so we can brief the crew.',
      preparationDetail:
        'Secure loose balcony items and inform security about our arrival. We bring drop cloths to protect interiors during window work.',
    }),
  },

  kottayam: {
    slug: 'cleaning-service-kottayam',
    locationName: 'Kottayam',
    heroImage: '/images/hero-deep-cleaning.png',
    hero: {
      eyebrow: 'Serving Kottayam Town, Kumarakom & Ettumanoor',
      title: 'Cleaning Service in Kottayam',
      description:
        'Professional deep cleaning for villas, apartments, institutions, and offices across Kottayam town, Kumarakom, and Ettumanoor.',
      highlights: [
        { icon: 'Waves', text: 'Backwater-safe products' },
        { icon: 'Calendar', text: 'Flexible family scheduling' },
        { icon: 'HeartHandshake', text: 'Local support team' },
      ],
      bookNowLocation: 'kottayam',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Waves',
        title: 'Backwater-Safe Cleaning',
        description:
          'We use moisture-controlled equipment to keep lakefront homes fresh without leaving dampness or residue.',
      },
      {
        icon: 'Calendar',
        title: 'Flexible Family Scheduling',
        description:
          'Plan early morning or late evening slots around school runs, parish activities, and office travel.',
      },
      {
        icon: 'HeartHandshake',
        title: 'Local Support Team',
        description:
          'Our supervisors live in Kottayam and coordinate permissions with apartment associations and institutions.',
      },
    ],
    serviceAreas: [
      'Kottayam Town',
      'Kanjikuzhy',
      'Nagampadam',
      'Kalathipady',
      'Ettumanoor',
      'Kumarakom Backwater Resorts',
      'Changanassery',
      'Pala',
      'Caritas Hospital Area',
      'Manarcad',
      'Pampady',
      'Vaikom Town',
    ],
    serviceAreaCallout: {
      emoji: '⛵',
      title: 'Staying by the backwaters?',
      description: 'Share jetty or parking access and we will protect your waterfront home while we clean.',
    },
    reviews: [
      {
        name: 'Priya Mathew',
        location: 'Kalathipady Villa',
        rating: 5,
        text: 'They removed red soil stains from our driveway and refreshed the entire home before family arrived.',
        date: 'October 2025',
      },
      {
        name: 'Fr. Joseph K.',
        location: 'Nagampadam Parish Office',
        rating: 5,
        text: 'Professional crew who respect schedules and finish meeting halls spotless for community events.',
        date: 'September 2025',
      },
      {
        name: 'Akhil George',
        location: 'Kumarakom Homestay',
        rating: 5,
        text: 'Guests complimented the freshness. They handled balconies and wooden furniture with care.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Keep Your Kottayam Home Guest-Ready',
      description:
        'Schedule a deep clean before family visits or parish gatherings and come back to a spotless space.',
      phoneText: 'Call 9995216660',
    },
    relatedLocationsHeading: 'Explore Nearby Kerala Service Locations',
    relatedLocationsDescription:
      'We support neighbouring districts with dedicated crews. Discover our cleaning teams across central Kerala.',
    relatedLocations: [
      {
        name: 'Alappuzha',
        description: 'Backwater homes & beach villas',
        href: '/cleaning-service-alappuzha',
      },
      {
        name: 'Thrissur',
        description: 'Cultural capital apartments & offices',
        href: '/cleaning-service-thrissur',
      },
      {
        name: 'Kochi',
        description: 'Metro city apartments & offices',
        href: '/cleaning-service-kochi',
      },
    ],
    seo: createSeoContent({
      locationName: 'Kottayam',
      introParagraphs: [
        'Kottayam balances academic institutions, busy town centres, and calm lakefront suburbs. Humidity and red soil dust make regular deep cleaning essential.',
        'Our crews operate from Kanjikuzhy and Ettumanoor to reach apartments, villas, and institutions quickly.',
      ],
      services: {
        lead: 'Pick a cleaning package tailored for town apartments, heritage bungalows, or resorts along Vembanad Lake.',
        bullets: [
          'Full home deep cleaning for villas around Kalathipady and Nagampadam',
          'Sanitisation plans for schools, colleges, and clinics near Kanjikuzhy and Devalokam',
          'Lakefront villa and homestay detailing across Kumarakom and Aymanam',
          'Sofa, mattress, and curtain shampooing for family homes in Kanjirappally and Pala',
          'Kitchen and bathroom descaling for apartments along MC Road',
          'Post-renovation cleanups for new constructions in Changanassery and Pampady',
        ],
      },
      lifestyle: [
        'Families prefer early morning starts before office hours, while institutions schedule afternoon slots after classes.',
        'We provide WhatsApp updates so NRIs and outstation owners can review work remotely.',
      ],
      coverage: [
        'We cover the MC Road corridor from Vaikom to Changanassery, including Kalathipady villas and Kumarakom resorts.',
      ],
      sameDay: [
        'Need an emergency spruce-up before guests arrive? Same-day slots are often available when you book before 2 PM.',
      ],
      booking: [
        'Book online in minutes or call us for custom schedules for institutions, clinics, and homestays.',
      ],
      conclusion:
        'Keep your Kottayam property fresh despite humidity. Reserve a slot or call +91 9995216660 today.',
    }),
    faq: createFaq({
      locationName: 'Kottayam',
      responseTime:
        'Most Kottayam requests are served within 24 hours. Backwater villas may need an extra hour for travel coordination.',
      areasMention: ['Kalathipady', 'Ettumanoor', 'Kumarakom', 'Changanassery'],
      pricingDetail:
        'Apartments and villas follow our published deep cleaning rates. Resorts or multi-storey institutions receive a quick site assessment before finalising quotes.',
      bookingDetail:
        'Use the Book Now button or call us. We coordinate with building associations and parish offices if permissions are required.',
      preparationDetail:
        'Secure boats, pets, and outdoor furniture. Share gate codes or jetty access so the crew can unload equipment smoothly.',
      extraQuestion: {
        question: 'Can you manage regular upkeep for clinics and schools?',
        answer:
          'Yes, we schedule weekly or monthly housekeeping plans for hospitals, clinics, and educational institutions across Kottayam.',
      },
    }),
  },

  alappuzha: {
    slug: 'cleaning-service-alappuzha',
    locationName: 'Alappuzha',
    heroImage: '/images/hero-deep-cleaning.png',
    hero: {
      eyebrow: 'Serving Alappuzha Beach, Punnamada & Mararikulam',
      title: 'Cleaning Service in Alappuzha',
      description:
        'Backwater-friendly deep cleaning for houseboats, resorts, villas, and homes across Alappuzha and the coastal belt.',
      highlights: [
        { icon: 'Anchor', text: 'Houseboat turnaround experts' },
        { icon: 'Sun', text: 'Salt-safe products' },
        { icon: 'Sparkles', text: 'Tourist-ready finish' },
      ],
      bookNowLocation: 'alappuzha',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Anchor',
        title: 'Houseboat Specialists',
        description:
          'Crew trained to refresh houseboats and lakeview resorts between guest check-ins and check-outs.',
      },
      {
        icon: 'Sun',
        title: 'Beach-Friendly Products',
        description: 'Salt and sand resistant cleaning keeps coastal villas and cafes fresh for guests.',
      },
      {
        icon: 'Users',
        title: 'Hospitality-Trained Staff',
        description:
          'Supervisors coordinate with resort managers and homestay owners to deliver seamless guest experiences.',
      },
    ],
    serviceAreas: [
      'Alappuzha Beach Ward',
      'Mullackal',
      'Punnamada Finishing Point',
      'Pathiramanal Jetty',
      'Kainakary',
      'Mararikulam',
      'Arthunkal',
      'Cherthala Town',
      'Kalavoor',
      'Thumpoly',
      'Ambalapuzha',
      'Alappuzha Medical College Area',
    ],
    serviceAreaCallout: {
      emoji: '🚤',
      title: 'Running a houseboat or homestay?',
      description:
        'Book express turnovers between guest departures and arrivals—complete with linens and upholstery refresh.',
    },
    reviews: [
      {
        name: 'Meera S.',
        location: 'Punnamada Houseboat',
        rating: 5,
        text: 'Impeccable detailing. They dried the deck fast and left the cabins hotel fresh before the next cruise.',
        date: 'October 2025',
      },
      {
        name: 'Abdul Rahman',
        location: 'Mararikulam Beach Villa',
        rating: 5,
        text: 'Salt stains disappeared and the pool deck sparkled. Guests loved the fragrance and hygiene.',
        date: 'September 2025',
      },
      {
        name: 'Lalitha Devi',
        location: 'Ambalapuzha Residence',
        rating: 5,
        text: 'They handled back-to-back festival visitors smoothly. Kitchen and bathrooms looked brand new.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Keep Your Alappuzha Property Guest-Ready',
      description:
        'Delight travellers and family alike with spotless interiors, fresh linens, and sparkling decks.',
      phoneText: 'Call 9995216660',
    },
    relatedLocationsHeading: 'Cleaning Teams Across Central Kerala',
    relatedLocationsDescription:
      'Need services outside Alappuzha? Explore our crews in nearby districts for consistent quality.',
    relatedLocations: [
      {
        name: 'Kottayam',
        description: 'Town & backwater households',
        href: '/cleaning-service-kottayam',
      },
      {
        name: 'Thrissur',
        description: 'Festival-ready temple town cleaning',
        href: '/cleaning-service-thrissur',
      },
      {
        name: 'Kochi',
        description: 'Metro apartments & commercial hubs',
        href: '/cleaning-service-kakkanad',
      },
    ],
    seo: createSeoContent({
      locationName: 'Alappuzha',
      introParagraphs: [
        'Tourism, humidity, and coastal winds make regular cleaning essential for Alappuzha homes, homestays, and houseboats.',
        'Our waterfront-trained crew manages linen care, deck drying, and marine-safe products to keep your property guest-ready year-round.',
      ],
      services: {
        lead: 'Choose from express turnovers for houseboats to full villa deep cleans along the coast.',
        bullets: [
          'Houseboat turnaround cleaning with linen change and deck drying',
          'Resort housekeeping support across Mararikulam and Arthunkal',
          'Deep cleaning for beachside villas and apartments near Alappuzha town',
          'Sofa, mattress, and curtain shampooing to beat humidity and odours',
          'Kitchen degreasing and chimney cleaning for seafood restaurants and cafes',
          'Post-event cleanups for wedding venues and lakeside resorts',
        ],
      },
      lifestyle: [
        'Homestay owners request mid-week slots between guest departures, while cafes prefer late-night refreshes after closing.',
        'We provide moisture control and dehumidification guidance to keep interiors fresh despite humidity.',
      ],
      coverage: [
        'We cover the entire Punnamada backwater belt from Finishing Point to Kainakary, plus beachside homes at Mararikulam and Arthunkal.',
      ],
      sameDay: ['Emergency cleanups for last-minute bookings are available when requested before noon.'],
      booking: ['Book online or WhatsApp us to coordinate with your houseboat schedule and guest check-ins.'],
      conclusion:
        'Delight every guest with a spotless Alappuzha experience. Reserve your cleaning slot or call +91 9995216660 now.',
    }),
    faq: createFaq({
      locationName: 'Alappuzha',
      responseTime:
        'We accommodate most requests within 24 hours. Houseboat turnovers can often be scheduled the same day between cruises.',
      areasMention: ['Punnamada', 'Mararikulam', 'Cherthala', 'Kainakary'],
      pricingDetail:
        'Standard rates apply for apartments and villas. Houseboats, resorts, and large venues receive a tailored quote based on size and turnaround time.',
      bookingDetail:
        'Use the Book Now button or call us to align with guest arrivals, boat docking times, or function schedules.',
      preparationDetail:
        'Dock the boat securely, switch off generators if required, and set aside fragile decor. We bring marine-safe products for decks and cabins.',
      extraQuestion: {
        question: 'Do you provide eco-friendly marine-safe products?',
        answer:
          'Yes, we use biodegradable, lake-safe cleaning solutions on decks and ensure run-off never affects the backwaters.',
      },
    }),
  },

  thrissur: {
    slug: 'cleaning-service-thrissur',
    locationName: 'Thrissur',
    heroImage: '/images/hero-deep-cleaning.png',
    hero: {
      eyebrow: 'Serving Thrissur Round, Guruvayur & Irinjalakuda',
      title: 'Cleaning Service in Thrissur',
      description:
        'Festival-ready deep cleaning for homes, apartments, halls, and businesses across Thrissur city and the cultural belt.',
      highlights: [
        { icon: 'Sparkles', text: 'Temple-town detailing' },
        { icon: 'Clock', text: 'Same-day festival support' },
        { icon: 'Shield', text: 'Verified professionals' },
      ],
      bookNowLocation: 'thrissur',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Sparkles',
        title: 'Temple-Town Detailing',
        description:
          'We handle brass polishing, rangoli-safe sweeping, and festival cleanups without disturbing sacred decor.',
      },
      {
        icon: 'Calendar',
        title: 'Festival-Smart Scheduling',
        description:
          'Crew rotations ensure dawn and late-night slots during Pooram, Onam, and church feast seasons.',
      },
      {
        icon: 'Shield',
        title: 'Background-Verified Crew',
        description:
          'Every professional is verified and supervisor-led, giving families and businesses complete peace of mind.',
      },
    ],
    serviceAreas: [
      'Swaraj Round',
      'Poothole',
      'Ayyanthole',
      'Chembukkavu',
      'Mannuthy',
      'Punkunnam',
      'Koorkenchery',
      'Westfort',
      'Guruvayur',
      'Chalakudy',
      'Irinjalakuda',
      'Kodungallur',
    ],
    serviceAreaCallout: {
      emoji: '🎉',
      title: 'Planning for Thrissur Pooram?',
      description:
        'Book festival-ready cleaning for homes, halls, and offices so you can focus on celebrations.',
    },
    reviews: [
      {
        name: 'Anjali Menon',
        location: 'Chembukkavu Apartment',
        rating: 5,
        text: 'They coordinated with our association and delivered a spotless finish before our Onam get-together.',
        date: 'October 2025',
      },
      {
        name: 'Vishnu Nair',
        location: 'Guruvayur Homestay',
        rating: 5,
        text: 'Guests checked in to freshly cleaned rooms even during peak pilgrim season. Excellent turnaround time.',
        date: 'September 2025',
      },
      {
        name: 'Sister Maria',
        location: 'Chalakudy Convent',
        rating: 5,
        text: 'Courtyard, chapel, and dorms were immaculate. Crew respected quiet hours and completed ahead of schedule.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Keep Your Thrissur Home Festival-Ready',
      description:
        'Stay celebration-ready with professional cleaning before and after major events and gatherings.',
      phoneText: 'Call 9995216660',
    },
    relatedLocationsHeading: 'Nearby Kerala Cleaning Locations',
    relatedLocationsDescription:
      'Explore our crews in neighbouring districts for multi-city service coverage across Kerala.',
    relatedLocations: [
      {
        name: 'Kottayam',
        description: 'Lakefront villas & academic townships',
        href: '/cleaning-service-kottayam',
      },
      {
        name: 'Alappuzha',
        description: 'Backwater homes & houseboats',
        href: '/cleaning-service-alappuzha',
      },
      {
        name: 'Kochi',
        description: 'Metro apartments & commercial hubs',
        href: '/cleaning-service-kakkanad',
      },
    ],
    seo: createSeoContent({
      locationName: 'Thrissur',
      introParagraphs: [
        "Thrissur is Kerala's cultural capital, with temples, churches, and auditoriums hosting events year-round. Quick, reliable cleaning keeps spaces ready for celebrations.",
        'Our crews stage from Ayyanthole and Westfort to reach Swaraj Round apartments, Guruvayur homestays, and Chalakudy villas on time.',
      ],
      services: {
        lead: "Pick from festival prep packages, deep cleaning, or commercial upkeep tailored for Thrissur's cultural calendar.",
        bullets: [
          'Deep cleaning for apartments and villas along Swaraj Round and Chembukkavu',
          'Temple, church, and auditorium detailing with brass and granite care',
          'Post-Pooram cleanups for homes and commercial spaces',
          'Sofa, carpet, and mattress shampooing for homestays near Guruvayur',
          'Office housekeeping for firms in Punkunnam, Westfort, and Poothole',
          'Move-in cleaning for new flats around Mannuthy and Irinjalakuda',
        ],
      },
      lifestyle: [
        'Families request late-night slots after cultural programmes, while businesses opt for early mornings before staff arrive.',
        'We coordinate with festival committees and building managers to respect prayer timings and noise restrictions.',
      ],
      coverage: [
        'We service the entire district—from Guruvayur to Kodungallur and Chalakudy—via the NH544 and coastal corridors.',
      ],
      sameDay: [
        'Same-day support is available during festival season when you call before noon, subject to crew availability.',
      ],
      booking: [
        'Reserve online or call our Thrissur coordinator to align cleaning with event rehearsals, poojas, or receptions.',
      ],
      conclusion:
        "Stay celebration-ready with Thrissur's most dependable cleaning team. Book your slot or call +91 9995216660 today.",
    }),
    faq: createFaq({
      locationName: 'Thrissur',
      responseTime:
        'Most requests are handled within 24 hours. During Pooram or festive weeks, we recommend booking at least 48 hours in advance.',
      areasMention: ['Swaraj Round', 'Guruvayur', 'Chalakudy', 'Irinjalakuda'],
      pricingDetail:
        'Standard packages follow our published rates. Large halls or multi-day festival cleanups receive customised quotations after a quick walkthrough.',
      bookingDetail:
        'Use Book Now or call us to match cleaning with temple timings, church services, or auditorium schedules.',
      preparationDetail:
        'Secure ornaments, festival decor, and instruments. Share association rules or curfew times so the crew can plan accordingly.',
      extraQuestion: {
        question: 'Do you handle post-festival cleanups like Thrissur Pooram?',
        answer:
          'Yes, we deploy larger crews for post-event cleanups, including waste removal, floor scrubbing, and brass polishing as required.',
      },
    }),
  },

  'fort-kochi': {
    slug: 'cleaning-service-fort-kochi',
    locationName: 'Fort Kochi',
    heroImage: '/images/hero-fort-kochi.png',
    hero: {
      eyebrow: 'Serving Fort Kochi Heritage Zone, Beach Road & Mattancherry',
      title: 'Cleaning Service in Fort Kochi',
      description:
        'Heritage-friendly cleaning for homestays, cafes, and residences across Fort Kochi, Mattancherry, and Beach Road.',
      highlights: [
        { icon: 'Anchor', text: 'Heritage-safe methods' },
        { icon: 'Sparkles', text: 'Boutique finish' },
        { icon: 'Clock', text: 'Flexible for guests' },
      ],
      bookNowLocation: 'fort-kochi',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Anchor',
        title: 'Heritage Care',
        description:
          'We protect wooden beams, antique furniture, and mosaic floors using gentle, heritage-safe products.',
      },
      {
        icon: 'Sun',
        title: 'Tourist-Ready Turnarounds',
        description: 'Homestays and cafes get quick refreshes between guest check-outs and new arrivals.',
      },
      {
        icon: 'Users',
        title: 'Art & Cafe Friendly',
        description:
          'Art galleries, cafes, and co-working spaces rely on us for weekly detailing without disturbing decor.',
      },
    ],
    serviceAreas: [
      'Princess Street',
      'Burger Street',
      'Rose Street',
      'Fort Kochi Beach Road',
      'Bastion Bungalow Vicinity',
      'Santa Cruz Basilica Area',
      'Mattancherry Palace',
      'Jew Town',
      'Dutch Cemetery Road',
      'Thoppumpady',
      'Pandikudy',
      'Kumbalangi Link Road',
    ],
    serviceAreaCallout: {
      emoji: '🎨',
      title: 'Running a homestay or art cafe?',
      description: 'Schedule post-checkout deep cleans to keep reviews glowing and guests delighted.',
    },
    reviews: [
      {
        name: 'Merin Zachariah',
        location: 'Princess Street Homestay',
        rating: 5,
        text: 'Guests love the freshness after every checkout. They handle antique furniture with extreme care.',
        date: 'October 2025',
      },
      {
        name: 'Ashwin Kuruvilla',
        location: 'Fort Kochi Beach Cafe',
        rating: 5,
        text: 'Quick night cleaning keeps our cafe spotless for sunrise breakfasts. Highly recommended.',
        date: 'September 2025',
      },
      {
        name: 'Sarah Mathew',
        location: 'Mattancherry Boutique',
        rating: 5,
        text: 'Excellent detailing on heritage flooring and decor. They respect our art pieces and displays.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Keep Fort Kochi Heritage Spaces Immaculate',
      description: 'Book flexible slots that align with guest arrivals, art shows, or cafe rush hours.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'Fort Kochi',
      introParagraphs: [
        "Fort Kochi's colonial architecture and bustling art scene demand specialised cleaning. Sea breeze, foot traffic, and frequent guests can wear down surfaces fast.",
        'Our heritage-aware crew uses pH-balanced products, microfiber techniques, and gentle equipment to preserve historic charm.',
      ],
      services: {
        lead: 'Choose deep cleaning, heritage detailing, or express turnarounds crafted for homestays, cafes, and galleries.',
        bullets: [
          'Homestay deep cleaning between guest check-outs',
          'Cafe and restaurant hygiene maintenance along Beach Road',
          'Art gallery dusting with display-safe tools',
          'Wooden floor polishing and brass restoration touch-ups',
          'Bathroom and kitchen sanitisation for boutique stays',
          'Post-event cleanups for heritage venues and courtyards',
        ],
      },
      lifestyle: [
        'Tourist seasons require fast turnarounds. We plan crews to refresh rooms within hours so you never miss a booking.',
        'Artists and cafe owners appreciate our flexible slots that avoid peak visitor timings.',
      ],
      coverage: [
        'We serve Fort Kochi, Mattancherry, Jew Town, Thoppumpady, and Kumbalangi link roads with equal attention.',
      ],
      sameDay: [
        'Emergency cleanups after a surprise guest departure? Same-day help is often available with two to three hours notice.',
      ],
      booking: [
        'Book online or message us on WhatsApp. Receive digital checklists, crew IDs, and photo reports after every service.',
      ],
      conclusion:
        'Delight every guest with a spotless Fort Kochi experience. Reserve your preferred slot or call +91 9995216660.',
    }),
    faq: createFaq({
      locationName: 'Fort Kochi',
      responseTime: 'We serve most requests within 24 hours. Peak tourist weeks may require early booking.',
      areasMention: ['Princess Street', 'Mattancherry', 'Jew Town', 'Thoppumpady'],
      pricingDetail:
        'Homestay packages start at our published rates. Heritage mansions or multi-room villas receive customised quotes after a walkthrough.',
      bookingDetail: 'Use our website or WhatsApp to align cleaning between check-out and check-in windows.',
      preparationDetail:
        'Secure fragile art, label antiques that need special care, and share any property rules before the crew arrives.',
      extraQuestion: {
        question: 'Do you use eco-friendly products?',
        answer:
          'Yes, we default to eco-friendly and heritage-safe solutions to protect wood, brass, and historic finishes.',
      },
    }),
  },

  kochi: {
    slug: 'cleaning-service-kochi',
    locationName: 'Kochi',
    heroImage: '/images/hero-kochi-city.png',
    seoMetaTitle:
      'Cleaning Service Kochi | Tools & Bubbles | Deep Cleaning, Home & Office | 4.8★ | 9995216660',
    seoMetaDescription:
      'Cleaning service Kochi: Tools & Bubbles — deep cleaning, house & office cleaning since 2009. 4.8★ Google, 230+ reviews, 10,000+ customers. Same-day slots across Kakkanad, Edappally, Kaloor & all Kochi. Call 9995216660.',
    seoGeoPlacename: 'Kochi, Kerala, India',
    hero: {
      eyebrow: 'Cleaning Service Kochi · Since 2009',
      title: 'Professional Cleaning Service Kochi — Homes, Offices & Deep Cleaning',
      description:
        'Get your home and office in Cochin cleaned by professionals. Same-day deep cleaning, house cleaning, office cleaning, sofa cleaning across Kakkanad, Kaloor, Palarivattom, Ernakulam, Edappally, and all major areas in Kochi.',
      highlights: [
        { icon: 'Shield', text: 'Insured & certified crew' },
        { icon: 'Clock', text: 'Same-day availability' },
        { icon: 'Star', text: '4.8★ Google · 10,000+ customers' },
      ],
      bookNowLocation: 'kochi',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Shield',
        title: 'Trusted Across Kochi',
        description:
          '15+ years serving Kochi and Greater Cochin. Background-verified staff, insured service, and supervisor check-ins for every booking.',
      },
      {
        icon: 'Sparkles',
        title: 'Deep Cleaning Experts',
        description:
          'Kitchen deep cleaning, bathroom sanitisation, sofa cleaning, carpet cleaning, and full home deep cleaning. We cover every corner.',
      },
      {
        icon: 'Calendar',
        title: 'Same-Day & Flexible Slots',
        description:
          'Book online or call 9995216660. Early morning to evening slots across Kakkanad, Edappally, Kaloor, MG Road, Vyttila, and all Kochi areas.',
      },
    ],
    serviceAreas: [
      'Kakkanad & Infopark',
      'Edappally & Lulu Mall',
      'Kaloor & JLN Stadium',
      'Palarivattom',
      'Ernakulam & MG Road',
      'Kadavanthra & Panampilly Nagar',
      'Vyttila Hub',
      'Kalamassery',
      'Aluva & Airport',
      'Marine Drive',
      'Fort Kochi & Mattancherry',
      'Willingdon Island',
    ],
    serviceAreaHeadline: 'We Cover Every Corner of Kochi',
    serviceAreaDescription:
      'Apartments, villas, offices, and commercial spaces across Kochi (Cochin) and Greater Ernakulam. Same-day deep cleaning, house cleaning, and office cleaning near you.',
    serviceAreaCallout: {
      emoji: '🏠',
      title: 'Serving all major areas in Kochi',
      description:
        'From Kakkanad to Fort Kochi, Edappally to Marine Drive – one trusted team for your home and office cleaning.',
    },
    reviews: [
      {
        name: 'Anjali Thomas',
        location: 'Kakkanad',
        rating: 5,
        text: 'Best cleaning service in Kochi. Booked same-day and the team was punctual and thorough. House looked brand new!',
        date: 'October 2025',
      },
      {
        name: 'Vivek Krishnan',
        location: 'Edappally',
        rating: 5,
        text: 'Professional and reliable. They deep cleaned our 3BHK near Lulu Mall better than any other service in Cochin.',
        date: 'September 2025',
      },
      {
        name: 'Lakshmi Nair',
        location: 'Kaloor',
        rating: 5,
        text: 'Tools & Bubbles has been our go-to for house cleaning in Kochi. Transparent pricing and excellent results every time.',
        date: 'August 2025',
      },
    ],
    reviewHeadline: 'What Kochi Customers Say',
    reviewDescription: 'Real feedback from residents and businesses across Kochi and Cochin.',
    relatedLocationsHeading: 'Cleaning Services by Area in Kochi',
    relatedLocationsDescription:
      'Find a dedicated page for your neighbourhood. We serve every major area in Kochi with the same quality and pricing.',
    relatedLocations: KOCHI_LOCATIONS,
    cta: {
      title: "Book Kochi's #1 Cleaning Service",
      description:
        'Join 10,000+ Kochi residents who rely on Tools & Bubbles for spotless homes and offices. Same-day slots available.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      ...createSeoContent({
        locationName: 'Kochi',
        introParagraphs: [
          'Your professional house cleaning service company in Kochi! We are dedicated to providing the highest quality cleaning solutions for homes and offices across Kochi (Cochin) and Greater Ernakulam.',
          'When it comes to house cleaning in Kochi, we understand the importance of maintaining a clean and healthy living environment. Our certified crew offers deep cleaning, window cleaning, professional housekeeping, domestic cleaning, office cleaning, carpet cleaning, toilet cleaning, kitchen cleaning, bathroom cleaning, room cleaning, floor cleaning, and septic tank cleaning.',
          "Tools & Bubbles has been Kochi's trusted choice for 15+ years. We serve Kakkanad, Kaloor, Palarivattom, Ernakulam, Edappally, Vyttila, MG Road, Marine Drive, Fort Kochi, and all major areas with same-day availability and 100% satisfaction guarantee.",
        ],
        services: {
          lead: 'Our cleaning services in Kochi include:',
          bullets: [
            'Deep cleaning for homes and offices across Kochi',
            'House cleaning and domestic cleaning in Cochin',
            'Kitchen deep cleaning and bathroom sanitisation',
            'Sofa cleaning, carpet cleaning, and upholstery care',
            'Office cleaning and commercial cleaning',
            'Same-day and scheduled slots in all Kochi areas',
          ],
        },
        lifestyle: [
          'Kochi residents and businesses choose us for punctuality, transparent pricing, and consistent quality. We sync with your schedule – early morning, weekend, or same-day.',
          'Our teams are familiar with apartment societies, villas, and commercial buildings across Kakkanad, Edappally, Kaloor, and the rest of Kochi.',
        ],
        coverage: [
          'We cover all major areas in Kochi: Kakkanad (Infopark, SmartCity), Edappally (Lulu Mall, Metro), Kaloor, Palarivattom, Kadavanthra, Vyttila, MG Road, Marine Drive, Fort Kochi, Aluva, Kalamassery, and more.',
        ],
        sameDay: [
          'Need cleaning today? Same-day slots are often available when you book in the morning. Call 9995216660 or book online.',
        ],
        booking: [
          'Book online in minutes or call 9995216660. WhatsApp support for instant confirmation and crew updates.',
        ],
        conclusion:
          'Ranked #1 for cleaning service in Kochi. Book your deep cleaning or house cleaning today – same-day slots available. Call +91 9995216660.',
      }),
      introHeading: 'Cleaning Service Kochi: Professional Home & Office Cleaning (2026 Guide)',
    },
    faq: createFaq({
      locationName: 'Kochi',
      responseTime:
        'Most Kochi requests are served the same day or within 24 hours. Book before 2 PM for best same-day availability.',
      areasMention: ['Kakkanad', 'Edappally', 'Kaloor', 'Palarivattom', 'MG Road', 'Vyttila'],
      pricingDetail:
        'Transparent online pricing for deep cleaning, house cleaning, and sofa cleaning. Apartments and villas follow our published rates; large offices or custom needs get a quick quote.',
      bookingDetail:
        'Use the Book Now button on this page or call 9995216660. Share your area (e.g. Kakkanad, Edappally) and we’ll confirm slots and crew.',
      preparationDetail:
        'No special preparation needed. Our crew brings equipment and supplies. Secure pets if needed and share gate or building access details.',
      extraQuestion: {
        question: 'Do you serve my area in Kochi?',
        answer:
          'We serve all major areas in Kochi and Cochin including Kakkanad, Edappally, Kaloor, Palarivattom, Kadavanthra, Vyttila, MG Road, Marine Drive, Fort Kochi, Aluva, and Kalamassery. Call us if your area is not listed.',
      },
    }),
  },

  thripunithura: {
    slug: 'cleaning-service-thripunithura',
    locationName: 'Thripunithura',
    heroImage: '/images/hero-villa.png',
    hero: {
      eyebrow: 'Serving Thripunithura, Hill Palace Road & Gandhinagar',
      title: 'Cleaning Service in Thripunithura',
      description:
        'Professional deep cleaning for homes and offices in Thripunithura, Ernakulam. Same-day slots near Hill Palace, Gandhinagar, and all Thripunithura areas. 4.9★ rated crew.',
      highlights: [
        { icon: 'Building', text: 'Hill Palace & residential hub' },
        { icon: 'Clock', text: 'Same-day availability' },
        { icon: 'Star', text: '4.9★ rated local crew' },
      ],
      bookNowLocation: 'thripunithura',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Shield',
        title: 'Trusted in Thripunithura',
        description:
          'Families and offices near Hill Palace Road and Gandhinagar rely on us for recurring deep cleaning and housekeeping.',
      },
      {
        icon: 'Calendar',
        title: 'Flexible Slots',
        description:
          'Book morning or evening slots that fit your schedule. We serve apartments, villas, and commercial spaces across Thripunithura.',
      },
      {
        icon: 'Sparkles',
        title: 'Same-Day Service',
        description:
          'Need cleaning today? Same-day deep cleaning and sofa cleaning often available when you book early.',
      },
    ],
    serviceAreas: [
      'Hill Palace Road',
      'Gandhinagar',
      'Thripunithura Town',
      'Sree Krishna Temple Road',
      'Poothotta',
      'Kumbalangi Road',
      'Thripunithura East',
      'Prasanth Nagar',
      'Kowdiar Lane',
      'Vazhakkala',
    ],
    reviews: [
      {
        name: 'Rajesh K.',
        location: 'Gandhinagar',
        rating: 5,
        text: 'Best cleaning service in Thripunithura. Punctual and thorough.',
        date: 'October 2025',
      },
      {
        name: 'Priya M.',
        location: 'Hill Palace Road',
        rating: 5,
        text: 'Deep cleaned our 3BHK before Onam. Highly recommend.',
        date: 'September 2025',
      },
      {
        name: 'Suresh N.',
        location: 'Thripunithura Town',
        rating: 5,
        text: 'Office cleaning every week. Professional and reliable.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Book Thripunithura Cleaning Now',
      description: 'Join thousands in Ernakulam who trust Tools & Bubbles.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'Thripunithura',
      introParagraphs: [
        'Professional cleaning service in Thripunithura, Ernakulam. We serve Hill Palace Road, Gandhinagar, and all Thripunithura areas with same-day deep cleaning, home cleaning, and office cleaning.',
        'Tools & Bubbles is Thripunithura’s trusted choice for house cleaning, sofa cleaning, and commercial cleaning. Transparent pricing, 4.9★ rating.',
      ],
      services: {
        lead: 'Deep cleaning, home cleaning, sofa cleaning, and office cleaning across Thripunithura.',
        bullets: [
          'Full home deep cleaning',
          'Kitchen & bathroom cleaning',
          'Sofa & carpet cleaning',
          'Office & commercial cleaning',
          'Same-day slots',
        ],
      },
      lifestyle: [
        'Thripunithura residents book us for regular deep cleaning and move-in cleanups. We coordinate with apartment associations and security.',
      ],
      coverage: [
        'We cover Thripunithura town, Gandhinagar, Hill Palace Road, Poothotta, and neighbouring areas in Ernakulam.',
      ],
      booking: ['Book online or call 9995216660. Same-day slots available when you book before 2 PM.'],
      conclusion: 'Book cleaning in Thripunithura today. Call +91 9995216660.',
    }),
    faq: createFaq({
      locationName: 'Thripunithura',
      responseTime: 'Same-day or next-day slots are usually available.',
      areasMention: ['Hill Palace Road', 'Gandhinagar', 'Thripunithura Town'],
      pricingDetail: 'Transparent online pricing. View rates on the booking form.',
      bookingDetail: 'Book Now on this page or call 9995216660.',
    }),
  },

  thrikkakara: {
    slug: 'cleaning-service-thrikkakara',
    locationName: 'Thrikkakara',
    heroImage: '/images/hero-apartment.png',
    hero: {
      eyebrow: 'Serving Thrikkakara, CUSAT & Infopark Corridor',
      title: 'Cleaning Service in Thrikkakara',
      description:
        'Deep cleaning and home cleaning in Thrikkakara, Ernakulam. Same-day service near CUSAT, Thrikkakara Temple, and apartment hubs. 4.9★ rated.',
      highlights: [
        { icon: 'Building', text: 'CUSAT & Infopark corridor' },
        { icon: 'Clock', text: 'Same-day slots' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
      bookNowLocation: 'thrikkakara',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Shield',
        title: 'Trusted Near CUSAT',
        description:
          'Students, faculty, and families in Thrikkakara rely on us for apartment and villa cleaning.',
      },
      {
        icon: 'Calendar',
        title: 'Flexible Booking',
        description: 'Weekday and weekend slots. Same-day deep cleaning when you book early.',
      },
      {
        icon: 'Sparkles',
        title: 'Professional Crew',
        description: 'Background-verified staff. Insured service. WhatsApp updates.',
      },
    ],
    serviceAreas: [
      'Thrikkakara Temple Road',
      'CUSAT Campus Area',
      'Infopark Extension',
      'Kakkanad Road',
      'Padamugal',
      'Thrikkakara North',
      'Edappally-Thrikkakara Road',
      'Vazhakkala',
    ],
    reviews: [
      {
        name: 'Anoop T.',
        location: 'Thrikkakara',
        rating: 5,
        text: 'Quick and efficient. Best cleaning service in Thrikkakara.',
        date: 'October 2025',
      },
      {
        name: 'Deepa S.',
        location: 'Near CUSAT',
        rating: 5,
        text: 'Same-day deep cleaning. Very satisfied.',
        date: 'September 2025',
      },
      {
        name: 'Vinu P.',
        location: 'Padamugal',
        rating: 5,
        text: 'Office cleaning every month. Professional team.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Book Thrikkakara Cleaning Now',
      description: 'Ernakulam’s #1 cleaning service. Same-day available.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'Thrikkakara',
      introParagraphs: [
        'Cleaning service in Thrikkakara, Ernakulam. Deep cleaning, home cleaning, and office cleaning near CUSAT and Thrikkakara Temple. Same-day slots.',
        'Tools & Bubbles serves Thrikkakara with professional house cleaning, sofa cleaning, and commercial cleaning. 4.9★ rating.',
      ],
      services: {
        lead: 'We offer deep cleaning, home cleaning, sofa cleaning, and office cleaning in Thrikkakara.',
        bullets: [
          'Home deep cleaning',
          'Kitchen & bathroom',
          'Sofa cleaning',
          'Office cleaning',
          'Same-day service',
        ],
      },
      lifestyle: [
        'Thrikkakara is a busy residential and institutional belt. We fit your schedule with flexible slots.',
      ],
      coverage: ['We cover Thrikkakara town, CUSAT area, Padamugal, and the Infopark-Kakkanad corridor.'],
      booking: ['Book online or call 9995216660. Same-day when you book before 2 PM.'],
      conclusion: 'Book cleaning in Thrikkakara. Call +91 9995216660.',
    }),
    faq: createFaq({
      locationName: 'Thrikkakara',
      responseTime: 'Same-day or next-day slots available.',
      areasMention: ['Thrikkakara Temple', 'CUSAT', 'Padamugal'],
      pricingDetail: 'See live pricing on our booking form.',
      bookingDetail: 'Book Now or call 9995216660.',
    }),
  },

  maradu: {
    slug: 'cleaning-service-maradu',
    locationName: 'Maradu',
    heroImage: '/images/hero-home.png',
    hero: {
      eyebrow: 'Serving Maradu, NH Bypass & Lake Road',
      title: 'Cleaning Service in Maradu',
      description:
        'Professional house cleaning and deep cleaning in Maradu, Ernakulam. Same-day service for apartments and villas near NH Bypass and Maradu Junction. 4.9★.',
      highlights: [
        { icon: 'Building', text: 'NH Bypass & lake-side' },
        { icon: 'Clock', text: 'Same-day availability' },
        { icon: 'Star', text: '4.9★ rated crew' },
      ],
      bookNowLocation: 'maradu',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Shield',
        title: 'Trusted in Maradu',
        description: 'Residents and offices in Maradu rely on us for deep cleaning and regular housekeeping.',
      },
      {
        icon: 'Calendar',
        title: 'Flexible Slots',
        description: 'Morning to evening slots. Same-day deep cleaning when you book early.',
      },
      {
        icon: 'Sparkles',
        title: 'Quality Assured',
        description: 'Insured, background-verified crew. WhatsApp updates and supervisor check-ins.',
      },
    ],
    serviceAreas: [
      'Maradu Junction',
      'NH Bypass Maradu',
      'Lake Road',
      'Maradu Town',
      'Kumbalam Road',
      'Aroor Road',
      'Nettoor',
      'Thrippunithura Road',
    ],
    reviews: [
      {
        name: 'Liji K.',
        location: 'Maradu',
        rating: 5,
        text: 'Excellent deep cleaning. Best in Maradu.',
        date: 'October 2025',
      },
      {
        name: 'Thomas M.',
        location: 'NH Bypass',
        rating: 5,
        text: 'Office cleaning every week. Very professional.',
        date: 'September 2025',
      },
      {
        name: 'Reshma P.',
        location: 'Lake Road',
        rating: 5,
        text: 'Same-day booking. House looked spotless.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Book Maradu Cleaning Now',
      description: 'Ernakulam’s trusted cleaning service. Same-day slots.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'Maradu',
      introParagraphs: [
        'Cleaning service in Maradu, Ernakulam. Deep cleaning, home cleaning, and office cleaning near Maradu Junction and NH Bypass. Same-day available.',
        'Tools & Bubbles serves Maradu with professional house cleaning, sofa cleaning, and commercial cleaning. 4.9★ rating.',
      ],
      services: {
        lead: 'Deep cleaning, home cleaning, sofa cleaning, and office cleaning in Maradu.',
        bullets: [
          'Full home deep cleaning',
          'Kitchen & bathroom',
          'Sofa cleaning',
          'Office cleaning',
          'Same-day service',
        ],
      },
      lifestyle: [
        'Maradu is a fast-growing residential and commercial corridor. We offer flexible slots and same-day service.',
      ],
      coverage: ['We cover Maradu Junction, NH Bypass sector, Lake Road, Nettoor, and nearby areas.'],
      booking: ['Book online or call 9995216660. Same-day when you book before 2 PM.'],
      conclusion: 'Book cleaning in Maradu. Call +91 9995216660.',
    }),
    faq: createFaq({
      locationName: 'Maradu',
      responseTime: 'Same-day or next-day slots usually available.',
      areasMention: ['Maradu Junction', 'NH Bypass', 'Lake Road'],
      pricingDetail: 'Transparent pricing on booking form.',
      bookingDetail: 'Book Now or call 9995216660.',
    }),
  },

  'north-paravur': {
    slug: 'cleaning-service-north-paravur',
    locationName: 'North Paravur',
    heroImage: '/images/hero-deep-cleaning.png',
    hero: {
      eyebrow: 'Serving North Paravur (Paravur), Coastal Ernakulam',
      title: 'Cleaning Service in North Paravur',
      description:
        'Professional deep cleaning and house cleaning in North Paravur (Paravur), Ernakulam. Same-day service for homes and offices. 4.9★ rated crew.',
      highlights: [
        { icon: 'Waves', text: 'Coastal Ernakulam' },
        { icon: 'Clock', text: 'Same-day slots' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
      bookNowLocation: 'north-paravur',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Shield',
        title: 'Trusted in Paravur',
        description:
          'Families and businesses in North Paravur rely on us for deep cleaning and housekeeping.',
      },
      {
        icon: 'Calendar',
        title: 'Flexible Booking',
        description: 'Weekday and weekend slots. Same-day when you book early.',
      },
      {
        icon: 'Sparkles',
        title: 'Professional Service',
        description: 'Insured crew. Transparent pricing. WhatsApp updates.',
      },
    ],
    serviceAreas: [
      'North Paravur Town',
      'Paravur Junction',
      'Chendamangalam',
      'Chowara',
      'Kodungallur Road',
      'Varapuzha Road',
      'Coastal Paravur',
    ],
    reviews: [
      {
        name: 'Sunil K.',
        location: 'North Paravur',
        rating: 5,
        text: 'Best cleaning service in Paravur. Very satisfied.',
        date: 'October 2025',
      },
      {
        name: 'Remya P.',
        location: 'Paravur Town',
        rating: 5,
        text: 'Deep cleaning before function. Excellent job.',
        date: 'September 2025',
      },
      {
        name: 'George M.',
        location: 'Chendamangalam',
        rating: 5,
        text: 'Regular house cleaning. Reliable and prompt.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Book North Paravur Cleaning Now',
      description: 'Serving coastal Ernakulam. Same-day available.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'North Paravur',
      introParagraphs: [
        'Cleaning service in North Paravur (Paravur), Ernakulam. Deep cleaning, home cleaning, and office cleaning. Same-day slots.',
        'Tools & Bubbles serves North Paravur and coastal Ernakulam with professional house cleaning and commercial cleaning. 4.9★.',
      ],
      services: {
        lead: 'Deep cleaning, home cleaning, and office cleaning in North Paravur.',
        bullets: [
          'Home deep cleaning',
          'Kitchen & bathroom',
          'Sofa cleaning',
          'Office cleaning',
          'Same-day service',
        ],
      },
      lifestyle: [
        'North Paravur residents and businesses book us for regular and one-time deep cleaning. We coordinate travel from Kochi.',
      ],
      coverage: [
        'We cover North Paravur town, Paravur Junction, Chendamangalam, and nearby coastal Ernakulam areas.',
      ],
      booking: ['Book online or call 9995216660. Same-day when you book before 2 PM.'],
      conclusion: 'Book cleaning in North Paravur. Call +91 9995216660.',
    }),
    faq: createFaq({
      locationName: 'North Paravur',
      responseTime: 'Same-day or next-day slots available.',
      areasMention: ['Paravur Town', 'Chendamangalam', 'Chowara'],
      pricingDetail: 'See pricing on booking form. Travel to Paravur included in quote.',
      bookingDetail: 'Book Now or call 9995216660.',
    }),
  },

  kumbalangi: {
    slug: 'cleaning-service-kumbalangi',
    locationName: 'Kumbalangi',
    heroImage: '/images/hero-deep-cleaning.png',
    hero: {
      eyebrow: 'Serving Kumbalangi, Backwater Village & Tourism',
      title: 'Cleaning Service in Kumbalangi',
      description:
        'Professional cleaning for homes, homestays, and resorts in Kumbalangi, Ernakulam. Deep cleaning and housekeeping for backwater properties. 4.9★.',
      highlights: [
        { icon: 'Waves', text: 'Backwater village' },
        { icon: 'Clock', text: 'Flexible slots' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
      bookNowLocation: 'kumbalangi',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Shield',
        title: 'Homestay & Resort Experts',
        description:
          'We clean homestays, resorts, and villas in Kumbalangi with care for wooden and waterfront spaces.',
      },
      {
        icon: 'Calendar',
        title: 'Flexible Timing',
        description: 'Book around guest check-out. We use moisture-safe methods for backwater humidity.',
      },
      {
        icon: 'Sparkles',
        title: 'Quality Finish',
        description: 'Tourist-ready cleanliness. Safe products for heritage and eco-sensitive areas.',
      },
    ],
    serviceAreas: [
      'Kumbalangi Village',
      'Kumbalangi Tourism Zone',
      'Backwater Resorts',
      'Homestay Lane',
      'Poothotta Road',
      'Thripunithura-Kumbalangi Road',
    ],
    reviews: [
      {
        name: 'Jose K.',
        location: 'Kumbalangi',
        rating: 5,
        text: 'Our homestay gets cleaned after every checkout. Guests love it.',
        date: 'October 2025',
      },
      {
        name: 'Mary T.',
        location: 'Backwater resort',
        rating: 5,
        text: 'Professional and careful with wooden furniture.',
        date: 'September 2025',
      },
      {
        name: 'Antony P.',
        location: 'Kumbalangi',
        rating: 5,
        text: 'Villa deep cleaning before family visit. Excellent.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Book Kumbalangi Cleaning Now',
      description: 'Backwater-friendly cleaning. Same-day where possible.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'Kumbalangi',
      introParagraphs: [
        'Cleaning service in Kumbalangi, Ernakulam. Deep cleaning for homestays, resorts, and homes in the backwater village. Same-day and scheduled slots.',
        'Tools & Bubbles serves Kumbalangi with professional house cleaning and homestay turnover cleaning. 4.9★.',
      ],
      services: {
        lead: 'Deep cleaning, homestay turnover, and house cleaning in Kumbalangi.',
        bullets: [
          'Homestay deep cleaning',
          'Villa & home cleaning',
          'Kitchen & bathroom',
          'Post-guest cleanup',
          'Same-day where possible',
        ],
      },
      lifestyle: [
        'Kumbalangi homestays and resorts need quick, quality turnarounds. We use backwater-safe products and methods.',
      ],
      coverage: ['We cover Kumbalangi village, backwater resorts, homestays, and Poothotta road area.'],
      booking: ['Book online or call 9995216660. Share property access and we’ll confirm slot.'],
      conclusion: 'Book cleaning in Kumbalangi. Call +91 9995216660.',
    }),
    faq: createFaq({
      locationName: 'Kumbalangi',
      responseTime: 'Slots available same-day or next-day. Homestays often book between check-outs.',
      areasMention: ['Kumbalangi Village', 'Backwater resorts', 'Poothotta'],
      pricingDetail: 'Pricing on booking form. Homestays and multi-room properties get custom quotes.',
      bookingDetail: 'Book Now or call 9995216660. Share jetty or road access details.',
    }),
  },

  angamaly: {
    slug: 'cleaning-service-angamaly',
    locationName: 'Angamaly',
    heroImage: '/images/hero-apartment.png',
    hero: {
      eyebrow: 'Serving Angamaly, Airport Highway & NH 544',
      title: 'Cleaning Service in Angamaly',
      description:
        'Professional deep cleaning and house cleaning in Angamaly, Ernakulam. Same-day service near Cochin Airport, NH 544, and Angamaly town. 4.9★.',
      highlights: [
        { icon: 'Building', text: 'Airport & highway hub' },
        { icon: 'Clock', text: 'Same-day slots' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
      bookNowLocation: 'angamaly',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Shield',
        title: 'Trusted Near Airport',
        description:
          'Hotels, offices, and homes near Angamaly and the airport corridor rely on us for cleaning.',
      },
      {
        icon: 'Calendar',
        title: 'Flexible Slots',
        description: 'Early morning to evening. Same-day deep cleaning when you book early.',
      },
      {
        icon: 'Sparkles',
        title: 'Professional Crew',
        description: 'Background-verified, insured. WhatsApp updates and supervisor check-ins.',
      },
    ],
    serviceAreas: [
      'Angamaly Town',
      'Cochin Airport Road',
      'NH 544 Corridor',
      'Nedumbassery',
      'Karukutty',
      'Kalamassery-Angamaly Road',
      'Aluva-Angamaly Road',
    ],
    reviews: [
      {
        name: 'Saji K.',
        location: 'Angamaly',
        rating: 5,
        text: 'Office cleaning near airport. Always on time.',
        date: 'October 2025',
      },
      {
        name: 'Mini T.',
        location: 'Nedumbassery',
        rating: 5,
        text: 'Home deep cleaning before guests. Excellent.',
        date: 'September 2025',
      },
      {
        name: 'Raju P.',
        location: 'Karukutty',
        rating: 5,
        text: 'Best cleaning service in Angamaly area.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Book Angamaly Cleaning Now',
      description: 'Serving airport corridor and Ernakulam. Same-day available.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'Angamaly',
      introParagraphs: [
        'Cleaning service in Angamaly, Ernakulam. Deep cleaning, home cleaning, and office cleaning near Cochin Airport and NH 544. Same-day slots.',
        'Tools & Bubbles serves Angamaly, Nedumbassery, and the airport corridor with professional cleaning. 4.9★.',
      ],
      services: {
        lead: 'Deep cleaning, home cleaning, and office cleaning in Angamaly.',
        bullets: [
          'Home deep cleaning',
          'Office & commercial',
          'Kitchen & bathroom',
          'Sofa cleaning',
          'Same-day service',
        ],
      },
      lifestyle: [
        'Angamaly is a major highway and airport hub. We offer flexible slots for homes, hotels, and offices.',
      ],
      coverage: ['We cover Angamaly town, Nedumbassery, airport road, Karukutty, and NH 544 corridor.'],
      booking: ['Book online or call 9995216660. Same-day when you book before 2 PM.'],
      conclusion: 'Book cleaning in Angamaly. Call +91 9995216660.',
    }),
    faq: createFaq({
      locationName: 'Angamaly',
      responseTime: 'Same-day or next-day slots available.',
      areasMention: ['Angamaly Town', 'Nedumbassery', 'Airport Road'],
      pricingDetail: 'See pricing on booking form.',
      bookingDetail: 'Book Now or call 9995216660.',
    }),
  },

  perumbavoor: {
    slug: 'cleaning-service-perumbavoor',
    locationName: 'Perumbavoor',
    heroImage: '/images/hero-deep-cleaning.png',
    hero: {
      eyebrow: 'Serving Perumbavoor, Eastern Ernakulam',
      title: 'Cleaning Service in Perumbavoor',
      description:
        'Professional deep cleaning and house cleaning in Perumbavoor, Ernakulam. Same-day service for homes and offices. 4.9★ rated crew.',
      highlights: [
        { icon: 'Building', text: 'Eastern Ernakulam hub' },
        { icon: 'Clock', text: 'Same-day slots' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
      bookNowLocation: 'perumbavoor',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Shield',
        title: 'Trusted in Perumbavoor',
        description: 'Families and businesses in Perumbavoor rely on us for deep cleaning and housekeeping.',
      },
      {
        icon: 'Calendar',
        title: 'Flexible Booking',
        description: 'Weekday and weekend slots. Same-day when you book early.',
      },
      {
        icon: 'Sparkles',
        title: 'Professional Service',
        description: 'Insured crew. Transparent pricing. WhatsApp updates.',
      },
    ],
    serviceAreas: [
      'Perumbavoor Town',
      'Perumbavoor Junction',
      'Muvattupuzha Road',
      'Kothamangalam Road',
      'Aluva-Perumbavoor Road',
      'Piravom Road',
      'Kadungalloor',
    ],
    reviews: [
      {
        name: 'Varghese K.',
        location: 'Perumbavoor',
        rating: 5,
        text: 'Best cleaning service in Perumbavoor. Satisfied.',
        date: 'October 2025',
      },
      {
        name: 'Thankam P.',
        location: 'Perumbavoor Town',
        rating: 5,
        text: 'Deep cleaning before wedding. Excellent job.',
        date: 'September 2025',
      },
      {
        name: 'Babu M.',
        location: 'Muvattupuzha Road',
        rating: 5,
        text: 'Regular house cleaning. Reliable team.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Book Perumbavoor Cleaning Now',
      description: 'Serving eastern Ernakulam. Same-day available.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'Perumbavoor',
      introParagraphs: [
        'Cleaning service in Perumbavoor, Ernakulam. Deep cleaning, home cleaning, and office cleaning. Same-day slots.',
        'Tools & Bubbles serves Perumbavoor and eastern Ernakulam with professional house cleaning and commercial cleaning. 4.9★.',
      ],
      services: {
        lead: 'Deep cleaning, home cleaning, and office cleaning in Perumbavoor.',
        bullets: [
          'Home deep cleaning',
          'Kitchen & bathroom',
          'Sofa cleaning',
          'Office cleaning',
          'Same-day service',
        ],
      },
      lifestyle: [
        'Perumbavoor residents and businesses book us for regular and one-time deep cleaning. We coordinate travel from Kochi.',
      ],
      coverage: ['We cover Perumbavoor town, junction, Muvattupuzha road, and Kadungalloor area.'],
      booking: ['Book online or call 9995216660. Same-day when you book before 2 PM.'],
      conclusion: 'Book cleaning in Perumbavoor. Call +91 9995216660.',
    }),
    faq: createFaq({
      locationName: 'Perumbavoor',
      responseTime: 'Same-day or next-day slots available.',
      areasMention: ['Perumbavoor Town', 'Junction', 'Muvattupuzha Road'],
      pricingDetail: 'See pricing on booking form.',
      bookingDetail: 'Book Now or call 9995216660.',
    }),
  },

  muvattupuzha: {
    slug: 'cleaning-service-muvattupuzha',
    locationName: 'Muvattupuzha',
    heroImage: '/images/hero-deep-cleaning.png',
    hero: {
      eyebrow: 'Serving Muvattupuzha, Central Kerala Town',
      title: 'Cleaning Service in Muvattupuzha',
      description:
        'Professional deep cleaning and house cleaning in Muvattupuzha, Ernakulam. Same-day service for homes and offices. 4.9★ rated crew.',
      highlights: [
        { icon: 'Building', text: 'Central Kerala town' },
        { icon: 'Clock', text: 'Same-day slots' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
      bookNowLocation: 'muvattupuzha',
      bookNowService: 'deep-cleaning',
    },
    whyChoose: [
      {
        icon: 'Shield',
        title: 'Trusted in Muvattupuzha',
        description: 'Families and businesses in Muvattupuzha rely on us for deep cleaning and housekeeping.',
      },
      {
        icon: 'Calendar',
        title: 'Flexible Booking',
        description: 'Weekday and weekend slots. Same-day when you book early.',
      },
      {
        icon: 'Sparkles',
        title: 'Professional Service',
        description: 'Insured crew. Transparent pricing. WhatsApp updates.',
      },
    ],
    serviceAreas: [
      'Muvattupuzha Town',
      'Muvattupuzha Junction',
      'Kothamangalam Road',
      'Thodupuzha Road',
      'Perumbavoor Road',
      'Vaikkom Road',
      'Koothattukulam',
    ],
    reviews: [
      {
        name: 'Santhosh K.',
        location: 'Muvattupuzha',
        rating: 5,
        text: 'Best cleaning service in Muvattupuzha. Very good.',
        date: 'October 2025',
      },
      {
        name: 'Latha P.',
        location: 'Muvattupuzha Town',
        rating: 5,
        text: 'Deep cleaning before function. Excellent.',
        date: 'September 2025',
      },
      {
        name: 'Ramesh M.',
        location: 'Junction',
        rating: 5,
        text: 'Office cleaning monthly. Professional team.',
        date: 'August 2025',
      },
    ],
    cta: {
      title: 'Book Muvattupuzha Cleaning Now',
      description: 'Serving Muvattupuzha and central Ernakulam. Same-day available.',
      phoneText: 'Call 9995216660',
    },
    seo: createSeoContent({
      locationName: 'Muvattupuzha',
      introParagraphs: [
        'Cleaning service in Muvattupuzha, Ernakulam. Deep cleaning, home cleaning, and office cleaning. Same-day slots.',
        'Tools & Bubbles serves Muvattupuzha with professional house cleaning and commercial cleaning. 4.9★.',
      ],
      services: {
        lead: 'Deep cleaning, home cleaning, and office cleaning in Muvattupuzha.',
        bullets: [
          'Home deep cleaning',
          'Kitchen & bathroom',
          'Sofa cleaning',
          'Office cleaning',
          'Same-day service',
        ],
      },
      lifestyle: [
        'Muvattupuzha residents and businesses book us for regular and one-time deep cleaning. We coordinate from Kochi.',
      ],
      coverage: ['We cover Muvattupuzha town, junction, Kothamangalam road, and Thodupuzha road area.'],
      booking: ['Book online or call 9995216660. Same-day when you book before 2 PM.'],
      conclusion: 'Book cleaning in Muvattupuzha. Call +91 9995216660.',
    }),
    faq: createFaq({
      locationName: 'Muvattupuzha',
      responseTime: 'Same-day or next-day slots available.',
      areasMention: ['Muvattupuzha Town', 'Junction', 'Kothamangalam Road'],
      pricingDetail: 'See pricing on booking form.',
      bookingDetail: 'Book Now or call 9995216660.',
    }),
  },
  ...NEIGHBOURHOOD_LOCATION_CONFIGS,
};

export type LocationKey = keyof typeof locationConfigs;

export const getLocationConfig = (key: LocationKey) => locationConfigs[key];

/** Map URL path slugs to canonical location page slug. */
export const PATH_SLUG_TO_CANONICAL: Record<string, string> = {
  'deep-cleaning-kakkanad': 'cleaning-service-kakkanad',
  'home-cleaning-kakkanad': 'cleaning-service-kakkanad',
  'home-cleaning-edappally': 'cleaning-service-edappally',
  'home-cleaning-kaloor': 'cleaning-service-kaloor',
  'home-cleaning-kalamassery': 'cleaning-service-kalamassery',
  'cleaning-service-mg-road-kochi': 'cleaning-service-mg-road',
  'home-cleaning-panampilly-nagar': 'cleaning-service-panampilly-nagar',
  'home-cleaning-kadavanthra': 'cleaning-service-kadavanthra',
  'home-cleaning-vyttila': 'cleaning-service-vyttila',
  'home-cleaning-palarivattom': 'cleaning-service-palarivattom',
  'home-cleaning-eloor': 'cleaning-service-eloor',
  'home-cleaning-willingdon-island': 'cleaning-service-willingdon-island',
  'home-cleaning-aluva': 'cleaning-service-aluva',
  'cleaning-service-broadway-kochi': 'cleaning-service-broadway',
  'cleaning-service-marine-drive-kochi': 'cleaning-service-marine-drive',
  'home-cleaning-fort-kochi': 'cleaning-service-fort-kochi',
  'home-cleaning-kochi': 'cleaning-service-kochi',
  'home-cleaning-thripunithura': 'cleaning-service-thripunithura',
  'cleaning-service-tripunithura': 'cleaning-service-thripunithura',
  'home-cleaning-tripunithura': 'cleaning-service-thripunithura',
  'home-cleaning-thrikkakara': 'cleaning-service-thrikkakara',
  'home-cleaning-maradu': 'cleaning-service-maradu',
  'home-cleaning-north-paravur': 'cleaning-service-north-paravur',
  'home-cleaning-kumbalangi': 'cleaning-service-kumbalangi',
  'home-cleaning-angamaly': 'cleaning-service-angamaly',
  'home-cleaning-perumbavoor': 'cleaning-service-perumbavoor',
  'home-cleaning-muvattupuzha': 'cleaning-service-muvattupuzha',
  'home-cleaning-kottayam': 'cleaning-service-kottayam',
  'home-cleaning-alappuzha': 'cleaning-service-alappuzha',
  'home-cleaning-thrissur': 'cleaning-service-thrissur',
  ...NEIGHBOURHOOD_HOME_CLEANING_ALIASES,
};

export function getCanonicalLocationSlug(slug: string): string {
  return PATH_SLUG_TO_CANONICAL[slug] || slug;
}

/** Canonical + alias paths used by App routing (so new neighbourhoods are not hardcoded twice). */
export function getLocationPagePaths(): string[] {
  const paths = new Set<string>();
  for (const cfg of Object.values(locationConfigs)) {
    paths.add(`/${cfg.slug}`);
  }
  for (const alias of Object.keys(PATH_SLUG_TO_CANONICAL)) {
    paths.add(`/${alias}`);
  }
  return [...paths];
}

/**
 * Resolve location page config from URL path slug (same pattern as getServiceConfig for service pages).
 * Use this in a single LocationPage component so config is driven by URL and all sections (pricing, landmarks) render.
 */
export function getLocationConfigBySlug(pathSlug: string): LocationPageConfig | null {
  const slug = pathSlug.replace(/^\//, '').split('/')[0] || '';
  const canonicalSlug = PATH_SLUG_TO_CANONICAL[slug] || slug;
  // Config `slug` may differ from public canonical (e.g. cleaning-service-broadway-kochi → cleaning-service-broadway).
  // Compare using the same canonicalization as LocationPageTemplate / sitemap URLs.
  const found =
    Object.values(locationConfigs).find((c) => {
      const publicCanonical = PATH_SLUG_TO_CANONICAL[c.slug] || c.slug;
      return publicCanonical === canonicalSlug;
    }) ?? null;
  return found;
}
